// frontend/src/components/PricingAutomationDashboard.jsx
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Panel from './common/Panel';
import DisabledReason from './common/DisabledReason';
import RefreshButton from './common/RefreshButton';
import SplitButton from './common/SplitButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StatTile from './common/StatTile';
import Pill from './common/Pill';
import { Link as RouterLink } from 'react-router-dom';
import { FS, RAIL_WIDTH, V2, hoverOnly } from '../theme/tokens'
import { alpha } from '@mui/material/styles';
import { bodenLage, artZusatz } from '../utils/mindestpreisWarnung';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../utils/format';
import { marketLabel } from '../utils/market';
import useReloadOnRecovery from '../serviceOutage/useReloadOnRecovery';

const API = process.env.REACT_APP_API_BASE_URL || '';

/**
 * 🔴 Z-3 (UI2 Teil 9, 2026-10-10) — `r.ok ? r.json() : null` loeste einen
 * HTTP-Fehler zu `null` auf: er WARF nicht, der `catch` feuerte nur bei
 * Transport- und JSON-Parsefehlern, und die Oberflaeche zeichnete den Ausfall
 * als „0" und „Last run: Never" (Foto des Owners, Regel 22 §„Unknown is never
 * 0"). Diese drei Stellen sind der EINE Defekt in drei Ketten.
 *
 * `holeJson` wirft bei `!r.ok`, damit die VORHANDENEN Fehlerzweige den 502
 * tragen. Die Meldung selbst erscheint nirgends — der jeweilige Zweig setzt
 * seinen eigenen Satz (kein rohes „HTTP 502" im Bild).
 */
const holeJson = (r) => {
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const formatTime = (isoString) => {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const diffMins = Math.floor((Date.now() - date) / 60000);
  const diffHours = Math.floor(diffMins / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return formatDate(date);
};

const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const TRIGGER_LABELS = {
  competitor_not_in_stock: 'Competitor out of stock',
  manual_override_active: 'Manual override active',
  suppressed: 'Suppressed',
};

// 🔴 EIN Muster fuer den Mindestpreis-Ausloeser, an EINER Stelle.
// Bis zum 2026-09-18 standen hier drei Muster nebeneinander: `formatTrigger`
// pruefte streng (`([\d.]+)$`), der Rueckfall-Filter und der Sucher unten nur
// das Praefix. Ein nicht parsbarer Ausloeser (`below_min_price:abc`) fiel damit
// aus BEIDEN Anzeigen heraus -- die eigene Zeile wegen `Number.isFinite`, die
// Grundzeile wegen des laxen Filters -- und die Karte zeigte klickfrei GAR
// KEINEN Grund, wo vorher der rohe Ausloeser stand. Regel-16-Gate, W2,
// 2026-09-18. Das MUSTER kann nicht mehr auseinanderlaufen.
//
// ⚠️ Die BEDINGUNGEN koennen es weiterhin, und das steht hier, weil der Satz
// darueber sonst mehr verspricht, als er haelt: es gibt zwei -- diese Regex
// und das `Number.isFinite(parseFloat(...))` in `unterMindestpreis`.
// `[\d.]+` laesst reine Punktfolgen zu, `parseFloat` nicht.
// `below_min_price:..` faellt darum aus BEIDEN Anzeigen: die Regex trifft
// (also fliegt der Ausloeser aus Zeile 2 und aus der Ausloeserliste),
// `Number.isFinite` schlaegt fehl (also entsteht Zeile 2b nicht).
// Gemessen vom Regel-16-Gate, Runde 2, 2026-09-18.
// KEINE Wache dagegen, und das ist Absicht: das Backend schreibt
// `f"below_min_price:{min_price}"` aus einem Zahlwert
// (`backend/pricing_automation.py`), eine reine Punktfolge kann dort nicht
// entstehen. Das Loch ist strikt kleiner als vor dem Fix und praktisch
// unerreichbar; eine Probe darauf bewachte einen Fall, den kein Erzeuger
// herstellen kann.
const MINDESTPREIS_AUSLOESER = /^below_min_price:([\d.]+)$/;

// 🔴 Der zweite Ausloeser mit einer eigenen Anzeige (Owner-Entscheid 2026-10-28:
// der Basispreis gehoert DE). Er wird NICHT uebersetzt, sondern AUSGEFILTERT:
// die Karte traegt fuer ihn eine eigene Zeile, und derselbe Grund darf nicht
// zweimal ohne Klick dastehen (Grill W6) — dieselbe Ueberlegung wie bei
// `below_min_price` darunter, nur ohne Zahl in der Meldung.
// 🔴 KEIN Eintrag in `TRIGGER_LABELS`: der Grund verschwindet aus der Liste,
// bevor sie gerendert wird; ein Eintrag dort waere toter Text.
const BASISPREIS_AUSLOESER = 'basispreis_gehoert_de';

// 🔴 W5 (Regel 16): der Basis-Markt steht im Text als ETIKETT, nicht als nackter
// Code — dieselbe Hausform wie Zeile 1 der Karte (`marketLabel`). Den Code
// fuehrt der Server (`pricing_core.BASIS_MARKT`), er kommt mit der Nutzlast;
// dieser Wert ist nur der Rueckfall fuer eine alte Serverfassung. EIN Literal,
// EINE Stelle — und keine zweite Ableitung des Riegels in der Oberflaeche.
const BASIS_MARKT_CODE = 'DE';
const BASIS_ETIKETT = marketLabel(BASIS_MARKT_CODE, BASIS_MARKT_CODE);

const formatTrigger = (t) => {
  if (!t) return t;
  if (TRIGGER_LABELS[t]) return TRIGGER_LABELS[t];

  // outlier:z_score:-101.13_exceeds_threshold; below_historical_min:558.95
  if (t.startsWith('outlier:')) {
    const parts = t.slice('outlier:'.length).split('; ');
    const labels = parts.map((p) => {
      const zm = p.match(/^z_score:([-\d.]+)_exceeds_threshold$/);
      if (zm) return `Z-score ${formatNumber(parseFloat(zm[1]), { digits: 1 })}`;
      const bm = p.match(/^below_historical_min:([\d.]+)$/);
      if (bm) return `Below 30d min (${formatNumber(parseFloat(bm[1]))})`;
      const am = p.match(/^above_historical_max:([\d.]+)$/);
      if (am) return `Above 30d max (${formatNumber(parseFloat(am[1]))})`;
      return p;
    });
    return `Outlier: ${labels.join(', ')}`;
  }
  // large_change:90.2%
  const lc = t.match(/^large_change:([\d.]+)%$/);
  if (lc) return `Large change: ${lc[1]}%`;
  // below_min_price:684.37
  const bmp = t.match(MINDESTPREIS_AUSLOESER);
  // 🔴 "calculated minimum", nicht "min price": dieser Ausloeser misst
  // `p.min_price` -- den KALKULATORISCHEN Mindestpreis. Bis zum 2026-09-15 hiess er
  // hier wie der durchgesetzte, den die Warnung darueber nennt. Zwei Zahlen,
  // ein Wort, auf demselben Bildschirm. Vom Regel-16-Gate gefunden.
  if (bmp) return `Below calculated minimum (${formatNumber(parseFloat(bmp[1]))})`;
  // stale_scrape:reason
  if (t.startsWith('stale_scrape:')) return `Stale data: ${t.slice('stale_scrape:'.length)}`;
  // diff_too_small:0.030
  const ds = t.match(/^diff_too_small:([\d.]+)$/);
  if (ds) return `Diff too small (${formatMoney(parseFloat(ds[1]))})`;
  return t;
};

const SOURCE_LABELS = {
  auto: 'Auto-applied (scheduler)',
  auto_applied: 'Auto-applied (scheduler)',
  manual_review_approved: 'Manually approved',
  manual_review_rejected: 'Rejected',
  ui: 'Manual (UI)',
  email_button: 'Email button',
};
const formatSource = (source) => SOURCE_LABELS[source] || source || '—';

const fmt = (value, code = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).format(value || 0);

const severityColor = (s) =>
  s === 'high' ? 'error.main' : s === 'medium' ? 'warning.main' : 'info.main';

// ── Shopify Push Status Badge ─────────────────────────────────────────────────

function ShopifyBadge({ pushed, error, hasVariantId, hasPriceListId }) {
  // Data too old or not tracked
  if (pushed === undefined || pushed === null) return null;

  if (pushed) {
    return (
      <Tooltip title="Price updated in Shopify">
        <Chip
          icon={<CloudDoneIcon sx={{ fontSize: '0.75rem !important' }} />}
          label="Shopify ✓"
          size="small"
          sx={{ height: 18, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.success.main, 0.12), color: 'success.dark', border: 'none' }}
        />
      </Tooltip>
    );
  }

  // Not pushed — find out why
  if (!hasVariantId || !hasPriceListId) {
    const missing = [!hasVariantId && 'variant_id', !hasPriceListId && 'price_list_id'].filter(Boolean).join(', ');
    return (
      <Tooltip title={`Missing in DB: ${missing} — price NOT synced to Shopify`}>
        <Chip
          icon={<HelpOutlineIcon sx={{ fontSize: '0.75rem !important' }} />}
          label="No IDs"
          size="small"
          sx={{ height: 18, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.warning.main, 0.12), color: 'warning.dark', border: 'none' }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={error ? `Shopify error: ${error}` : 'Shopify push failed'}>
      <Chip
        icon={<CloudOffIcon sx={{ fontSize: '0.75rem !important' }} />}
        label="Shopify ✗"
        size="small"
        sx={{ height: 18, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.error.main, 0.12), color: 'error.dark', border: 'none' }}
      />
    </Tooltip>
  );
}

// ── Change Detail Modal ───────────────────────────────────────────────────────

function ChangeDetailModal({ change, open, onClose }) {
  if (!change) return null;
  const diff = (change.new_price ?? 0) - (change.old_price ?? 0);
  const pct = change.old_price ? (diff / change.old_price) * 100 : 0;
  const isUp = pct >= 0;

  const shopifyOk = change.shopify_pushed === true;
  const shopifyMissingIds = !change.has_variant_id || !change.has_price_list_id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Price Change Detail</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close price change detail"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Product</Typography>
            <Typography variant="body2" fontWeight={600}>
              {change.product_name || change.sku}
              {change.variant_name ? ` · ${change.variant_name}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{change.sku}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Market</Typography>
            <Typography variant="body2">{marketLabel(change.market_code, '—')}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Price Change</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                {fmt(change.old_price, change.currency_code)}
              </Typography>
              <Typography variant="body2" color="text.secondary">→</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: 'success.main' }}>
                {fmt(change.new_price, change.currency_code)}
              </Typography>
              <Chip
                label={`${isUp ? '+' : ''}${formatPercent(pct / 100, { digits: 1 })}`}
                size="small"
                sx={{
                  height: 20, fontSize: FS.xxs,
                  bgcolor: isUp ? t => alpha(t.palette.success.main, 0.12) : t => alpha(t.palette.error.main, 0.12),
                  color: isUp ? 'success.dark' : 'error.dark',
                }}
              />
            </Box>
          </Box>

          {/* Cheapest competitor — reason for price change */}
          {change.competitor_shop && (
            <Box sx={{
              p: 1.5, borderRadius: 2,
              bgcolor: t => alpha(t.palette.warning.main, 0.08),
              border: '1px solid',
              borderColor: t => alpha(t.palette.warning.main, 0.35),
            }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: 'warning.dark', display: 'block', mb: 0.5 }}>
                Cheapest Competitor
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.dark' }}>
                  {change.competitor_shop}
                </Typography>
                {change.competitor_price != null && (
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'warning.dark' }}>
                    {fmt(change.competitor_price, change.currency_code || 'EUR')}
                  </Typography>
                )}
              </Box>
              {/* Phase 3.3 note — CartAwarePriceBadge intentionally NOT rendered here.
                  This modal shows historical price-change-log rows from
                  /api/pricing/changes; the price_change_log table doesn't carry
                  sticker_price yet. Wiring the badge here would be dead code.
                  Follow-up: docs/ideas/2026-05-15-pricing-changelog-sticker.md */}
            </Box>
          )}

          {/* Shopify sync status */}
          <Box sx={{
            p: 1.5, borderRadius: 2,
            bgcolor: shopifyOk ? t => alpha(t.palette.success.main, 0.08) : shopifyMissingIds ? t => alpha(t.palette.warning.main, 0.08) : t => alpha(t.palette.error.main, 0.08),
            border: '1px solid',
            borderColor: shopifyOk ? t => alpha(t.palette.success.main, 0.4) : shopifyMissingIds ? t => alpha(t.palette.warning.main, 0.4) : t => alpha(t.palette.error.main, 0.4),
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {shopifyOk
                ? <CloudDoneIcon sx={{ color: 'success.main', fontSize: '1.1rem' }} />
                : <CloudOffIcon sx={{ color: shopifyMissingIds ? 'warning.main' : 'error.main', fontSize: '1.1rem' }} />}
              <Box>
                <Typography variant="caption" fontWeight={700} display="block"
                  sx={{ color: shopifyOk ? 'success.dark' : shopifyMissingIds ? 'warning.dark' : 'error.dark' }}>
                  {shopifyOk
                    ? 'Price updated in Shopify'
                    : shopifyMissingIds
                      ? 'Not synced — missing product IDs in DB'
                      : 'Shopify sync failed'}
                </Typography>
                {!shopifyOk && shopifyMissingIds && (
                  <Typography variant="caption" color="text.secondary">
                    {!change.has_variant_id && 'variant_id missing. '}
                    {!change.has_price_list_id && 'price_list_id missing. '}
                    Run a product sync to populate these.
                  </Typography>
                )}
                {!shopifyOk && !shopifyMissingIds && change.shopify_error && (
                  <Typography variant="caption" color="error">{change.shopify_error}</Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Source</Typography>
            <Typography variant="body2">{formatSource(change.source)}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">Changed at</Typography>
            <Typography variant="body2">{formatDateTime(change.changed_at)}</Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Stat Detail Modal ─────────────────────────────────────────────────────────

/**
 * Generic modal for clicking a KPI stat number.
 * `fetchUrl` — if provided, lazy-fetches changes when opened.
 * `items`    — if provided (review queue), uses pre-loaded data.
 * `itemType` — 'change' | 'review'
 */
function StatDetailModal({ open, onClose, title, subtitle, fetchUrl, items: preloadedItems, itemType = 'change', onOpenDetail }) {
  const [ladeFehler, setLadeFehler] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔴 Der Rumpf des Ladeeffekts steht seit Nachbesserung 2 in einer benannten
  // Funktion (E-8, 2026-10-11). Nur so hat `useReloadOnRecovery` einen
  // BESTEHENDEN Ladeweg zum Anhaengen — der Abruf selbst bleibt woertlich
  // derselbe, es kommt kein zweiter dazu.
  //
  // 🔴 R15-W2 (Nachbesserung 3, 2026-10-11): `useReloadOnRecovery(laden)` ruft
  // `laden()` DIREKT und VERWIRFT dessen Aufraeumfunktion. Schliesst jemand den
  // Dialog waehrend des Erholungsabrufs, schrieb der `setState` danach in eine
  // abgehaengte Ansicht (mit React 18 ein No-op — aber ein Abruf, der nach dem
  // Schliessen noch schreibt, ist genau das Fenster, das diese Karte zumacht).
  // Der Abbruchzeiger ist der EINE Ort, an dem der laufende Abruf haengt: jeder
  // neue Lauf bricht den vorigen ab — der Erholungsabruf den Effekt-Abruf und
  // umgekehrt.
  const laufRef = useRef(null);

  const laden = useCallback(() => {
    if (laufRef.current) { laufRef.current(); laufRef.current = null; }
    if (!open) { setItems([]); return undefined; }
    if (preloadedItems) { setItems(preloadedItems); return undefined; }
    if (!fetchUrl) return undefined;
    let cancelled = false;
    const abbrechen = () => { cancelled = true; };
    laufRef.current = abbrechen;
    // 🔴 Der Fehlerzustand wird beim NEULADEN zurueckgesetzt (E-8): sonst
    // bliebe „The list could not be loaded." nach der Erholung stehen, auch
    // wenn der Abruf gelingt — der Neuladevorgang haette dann nichts geheilt.
    setLadeFehler(false);
    setLoading(true);
    fetch(`${API}${fetchUrl}`)
      // 🔴 `holeJson` statt `(r.ok ? r.json() : null)` (Z-3): ein 502 loeste zu
      // `null` auf, `setItems` wurde uebersprungen und die Liste erschien LEER
      // statt fehlerhaft — derselbe Defekt wie in `LastRunPanel`.
      .then(holeJson)
      .then((d) => { if (!cancelled && d) setItems(d.changes || d.items || []); })
      // 🔴 Kein stiller `.catch(() => {})`. Ein Fehlschlag sah bis hierher aus
      // wie "keine Daten" -- die Musterbibliothek verbietet diese Form woertlich.
      .catch(() => { if (!cancelled) setLadeFehler(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      abbrechen();
      if (laufRef.current === abbrechen) laufRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchUrl]);

  useEffect(() => laden(), [laden]);

  // 🔴 R15-W2, zweite Haelfte: beim AUSHAENGEN bricht der JEWEILS letzte Lauf ab.
  // Der Aufraeumer des Effekts oben gehoert zu SEINEM Aufruf — einen Lauf, den
  // `useReloadOnRecovery` gestartet hat, kennt er nicht. Genau der ist der
  // unbedeckte Fall: Dialog offen, Erholung, dann Schliessen (der Dialog wird
  // bedingt montiert, `{statModal && …}` — Schliessen heisst Aushaengen).
  useEffect(() => () => {
    if (laufRef.current) { laufRef.current(); laufRef.current = null; }
  }, []);

  // 🔴 UI2 Teil 9, Karte Z-1c, E-8 (Nachbesserung 2, 2026-10-11): nach der
  // Erholung laedt auch DIESER Lader neu. Er haengt an keinem Takt und wird nur
  // beim Oeffnen gefahren; stand er im Ausfall offen, blieb sein Fehlerzustand
  // nach der Erholung stehen, bis jemand den Dialog schloss und neu oeffnete.
  useReloadOnRecovery(laden);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close detail"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : ladeFehler ? (
          // 🔴 Sichtbarer Fehlschlag statt "keine Daten". Bis zum 2026-09-17
          // verschluckte ein `.catch(() => {})` den Fehler, und ein 500 sah aus
          // wie eine leere Liste.
          <Alert severity="error" sx={{ fontSize: FS.md }}>
            The list could not be loaded.
          </Alert>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No items to show.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {items.map((item, i) => (
              itemType === 'review' ? (
                <ReviewItem
                  key={item.id ?? i}
                  {...item}
                  triggers={item.review_reasons}
                />
              ) : (
                <PriceChange
                  key={item.id ?? i}
                  {...item}
                  onClick={onOpenDetail ? () => { onClose(); onOpenDetail(item); } : undefined}
                />
              )
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── small sub-components ──────────────────────────────────────────────────────

function SectionToggle({ icon, label, count, expanded, onToggle, badge }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon}
        {label} ({count})
        {badge}
      </Typography>
      <IconButton size="small" onClick={onToggle}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}

function PriceChange({ sku, market_code, product_name, variant_name, old_price, new_price, currency_code = 'EUR', meta, shopify_pushed, shopify_error, has_variant_id, has_price_list_id, competitor_shop, competitor_price, onClick }) {
  const diff = (new_price ?? 0) - (old_price ?? 0);
  const pct = old_price ? (diff / old_price) * 100 : 0;
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      {...(onClick && {
        // 🔴 Bis zum 2026-09-17 war dies ein `Paper` mit `onClick` und sonst
        // nichts -- und der EINZIGE Weg ins Detail. Per Tastatur unerreichbar.
        // Das Muster macht `StatTile` im selben Repo richtig; hier fehlte es.
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
        },
      })}
      sx={{
        p: 1.5, borderLeft: `${RAIL_WIDTH}px solid`,
        borderLeftColor: shopify_pushed === false ? 'warning.main' : 'success.main',
        ...(onClick && {
          cursor: 'pointer',
          transition: 'background 0.15s',
          ...hoverOnly({ bgcolor: 'rgba(16,185,129,0.04)' }),
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {product_name || sku}{variant_name ? ` · ${variant_name}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {marketLabel(market_code, '—')}
            {meta ? ` · ${meta}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: FS.xxs }}>
              {sku}
            </Typography>
            <ShopifyBadge
              pushed={shopify_pushed}
              error={shopify_error}
              hasVariantId={has_variant_id}
              hasPriceListId={has_price_list_id}
            />
          </Box>
          {competitor_shop && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: FS.xxs, display: 'block', mt: 0.25 }}>
              Cheapest: <strong>{competitor_shop}</strong>
              {competitor_price != null ? ` · ${fmt(competitor_price, currency_code)}` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', ml: 1, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
            {fmt(old_price, currency_code)}
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'success.main' }}>
            {fmt(new_price, currency_code)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({pct > 0 ? '+' : ''}{formatPercent(pct / 100, { digits: 1 })})
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function ReviewItem({ id, sku, market_id, market_code, product_name, variant_name, current_price, recommended_price, currency_code = 'EUR', severity, triggers, approvable, basis_market_id, basis_market_code, competitor_shop, competitor_price, competitor_url, market_url, product_handle, created_at, expires_at, historical_analysis, scrape_status, onDecide, deciding }) {
  const [expanded, setExpanded] = useState(false);
  const diff = (recommended_price ?? 0) - (current_price ?? 0);
  const pct = current_price ? (diff / current_price) * 100 : 0;

  const hist = historical_analysis || {};
  const range = hist.price_range_30d || null;
  const scrape = scrape_status || {};
  const hasScrape = scrape.is_fresh !== undefined || Boolean(scrape.reason);
  // Traegt seit dem 2026-09-18 nur noch `hasContext`: ob es ueberhaupt
  // etwas aufzuklappen gibt. Angezeigt wird `reasonsOhneMindestpreis` -- der
  // Mindestpreis steht ohne Klick in Zeile 2b und nicht noch einmal darunter.
  // 🔴 Der Hinweis ist eine Zeile wie jede andere, nur NICHT freigebbar. Der
  // Server liefert die Ableitung (`ist_basis_markt(market_code) or
  // price_list_id IS NOT NULL`) — die Oberflaeche rechnet sie nicht nach.
  //
  // 🔴 W6 (Regel 16): FEHLEND heisst NICHT freigebbar, nicht „freigebbar".
  // Bis hierher stand `approvable = true` als Vorgabe — eine gekuerzte Nutzlast
  // oder eine alte Serverfassung haette „Accept" wieder auf die INT-Zeile
  // gestellt, also genau den Zustand vom 27.10. Ein Riegel, der bei fehlender
  // Auskunft oeffnet, ist die falsche Richtung. Beide Erzeuger im Repo liefern
  // das Feld (`pricing_api` je Zeile, `pricing_automation` am Lauf-Ergebnis).
  const istHinweis = approvable !== true;
  const basisCode = basis_market_code || BASIS_MARKT_CODE;
  const basisLabel = marketLabel(basisCode, basisCode);
  // W9: der Grund haengt per `aria-describedby` an den FOKUSSIERBAREN Knoepfen
  // dieser Karte (Details, Reject, Later) — nicht am umschliessenden `div`. Ein
  // `aria-describedby` an einem nicht fokussierbaren Container wird nicht an die
  // Kinder vererbt; wer per Tab auf „Reject" landet, hoerte den Satz sonst nie.
  // Die drei Knoepfe tragen deshalb dasselbe `aria-describedby={istHinweis ?
  // hinweisId : undefined}` (R16-C1).
  const hinweisId = useId();
  const reasons = (triggers || []).map(formatTrigger).filter(Boolean);
  const validUrl = typeof competitor_url === 'string' && /^https?:\/\//.test(competitor_url);
  const undercut = (competitor_price != null && recommended_price != null)
    ? (competitor_price - recommended_price) : null;

  // 🔴 EIN Satz Klartext statt n Chips plus einer Wiederholung darunter.
  // Bis zum 2026-09-17 standen Schweregrad und Ausloeser als Chips da UND
  // gleich darunter noch einmal als "Why flagged" -- zwei Angaben, drei
  // Anzeigen. Der Schweregrad steckt jetzt in der linken Randfarbe und
  // ausgeschrieben im Detail; die Farbe traegt ihn NICHT allein.
  // 🔴 Grill Runde 3, W-f: derselbe Ausloeser darf nicht ZWEIMAL ohne Klick
  // dastehen. Ohne Wettbewerber faellt `grundsatz` unten auf
  // `reasons.join(' · ')` zurueck und liefert `Below calculated minimum
  // (551.56)` -- daneben stuende die neue deutsche Zeile. Deshalb rechnet der
  // Rueckfall OHNE diesen Ausloeser; die eigene Zeile traegt ihn.
  const reasonsOhneMindestpreis = (triggers || [])
    .filter((x) => !MINDESTPREIS_AUSLOESER.test(x))
    // 🔴 W6: derselbe Grund darf nicht zweimal dastehen. Er bekommt unten eine
    // EIGENE Zeile, also faellt er hier aus der Liste — sonst stuende
    // „basispreis_gehoert_de" einmal roh und einmal erklaert.
    .filter((x) => String(x) !== BASISPREIS_AUSLOESER)
    .map(formatTrigger).filter(Boolean);

  const grundsatz = (() => {
    if (competitor_shop && undercut != null && undercut > 0) {
      return `Undercuts ${competitor_shop} (${fmt(competitor_price, currency_code)}) by ${fmt(undercut, currency_code)}`;
    }
    if (reasonsOhneMindestpreis.length) return reasonsOhneMindestpreis.join(' · ');
    return null;
  })();

  // 🔴 EIGENE Zeile, nicht in `grundsatz` verkettet (Owner-Entscheid O-B,
  // 2026-09-18: *„Klick bleibt, aber nichts blockiert mehr."*). Verketten
  // machte `PreisempfehlungPosten.test.jsx` rot -- die Probe zaehlt den
  // Unterbietungssatz genau einmal -- und holte die Dreifachanzeige halb
  // zurueck, die #1781 bewusst abgeschafft hat.
  //
  // 🔴 „calculated minimum": auf DERSELBEN Flaeche steht die Warnung zum
  // DURCHGESETZTEN Mindestpreis (`mindestpreis_warnung`, aus
  // `COALESCE(mp.min_price, p.min_price)`). Zwei Zahlen unter einem Wort auf
  // einem Bildschirm sind genau der Befund, den das Regel-16-Gate am
  // 2026-09-15 gefunden hat; deshalb heisst der Ausloeser-Text seither auch
  // `Below calculated minimum` und nicht `Below min price`.
  //
  // 🟢 Nachgezogen am 2026-10-07 (UI-Durchgang 2, E7): der Satz stand bis
  // hierher auf Deutsch („Unter dem kalkulatorischen Mindestpreis (…)").
  // Der Owner hat am 2026-08-01 die gesamte Tool-UI englisch gesetzt
  // (Ausnahme nur LLM-Texte und Kundenkommunikation), Formate bleiben
  // deutsch — die Zahl in der Klammer kommt unveraendert aus `fmt(…, 'EUR')`.
  // Die Begruendung dieses Kommentars gilt wortgleich weiter, nur das
  // Wort, um das es geht, heisst jetzt englisch.
  //
  // 🔴 Fest 'EUR', nicht `currency_code`: `p.min_price` IST eine EUR-Zahl
  // (`docs/features/pricing.md`), und der Ausloeser kann auf einer
  // Fremdwaehrungszeile ueberhaupt nicht entstehen -- `_vergleichbar` steht in
  // der Bedingung (`pricing_automation.py:482`; `:481` ist die Zuweisung von
  // `min_price`). Eine Altzeile aus der Zeit vor der Schranke laeuft nach
  // spaetestens sieben Tagen (`expires_at`) aus.
  //
  // 🔴 EIN Regex-Aufruf, keine Zeichenkette daneben. Bis zum 2026-09-18 stand
  // hier `t.slice('below_min_price:'.length)` -- das Praefix also ein VIERTES
  // Mal, diesmal als Literal statt als Muster (Regel-15-Tor Runde 2, W-4).
  // Die Konstante faengt die Zahl bereits als Gruppe 1; sie hier noch einmal
  // abzuschneiden koppelte die Leseseite an eine Schreibweise, die die
  // Konstante jederzeit aendern darf. Nebeneffekt, der die Sache erst rund
  // macht: `match(...)[1]` gibt es nur, wenn das STRENGE Muster trifft --
  // ein laxer Sucher koennte hier keine Zahl mehr erfinden. Die alte Bauform
  // haette aus `below_min_price:12abc` ein `12,00 €` gemacht, das nirgends
  // herkommt.
  const unterMindestpreis = (() => {
    const treffer = (triggers || [])
      .map((x) => String(x).match(MINDESTPREIS_AUSLOESER)).find(Boolean);
    if (!treffer) return null;
    const zahl = parseFloat(treffer[1]);
    if (!Number.isFinite(zahl)) return null;
    return `Below the calculated minimum price (${fmt(zahl, 'EUR')})`;
  })();

  // Alter bzw. Ablauf -- nie beide. "Aktuell sein MUSS" ist die Owner-Vorgabe,
  // und bis hierher las `ReviewItem` weder `created_at` noch `expires_at`.
  const alterText = (() => {
    const jetzt = Date.now();
    const ab = expires_at ? new Date(expires_at).getTime() : null;
    if (ab && ab - jetzt < 12 * 3600 * 1000) {
      const h = Math.max(0, Math.round((ab - jetzt) / 3600000));
      return { text: `expires in ${h} h`, dringend: true };
    }
    if (!created_at) return null;
    const h = Math.round((jetzt - new Date(created_at).getTime()) / 3600000);
    return { text: h < 1 ? 'just now' : `${h} h ago`, dringend: false };
  })();

  // Die drei Absprungpunkte. 🔴 Fehlt ein Ziel, steht es GRAU mit Grund da --
  // nie weggelassen, nie stillschweigend umgeleitet. Ein verschwindender Link
  // lehrt "da ist manchmal nichts", ein grauer mit Grund lehrt WARUM.
  const ziele = [
    (sku && market_id)
      ? { art: 'intern', nach: `/products/${sku}/markets/${market_id}`, text: `Our product (${marketLabel(market_code, '—')})` }
      : { art: 'fehlt', text: 'Our product', grund: 'SKU or market missing' },
    validUrl
      ? { art: 'extern', nach: competitor_url, text: `Competitor: ${competitor_shop || 'page'}` }
      : { art: 'fehlt', text: competitor_shop ? `Competitor: ${competitor_shop}` : 'Competitor', grund: 'no page on file' },
    (market_url && product_handle)
      ? { art: 'extern', nach: `${String(market_url).replace(/\/$/, '')}/products/${product_handle}`, text: 'Customer page' }
      // Die Marktadresse wird vom Sync geschrieben (`sync_markets.py`) und ist
      // fuer DE, AT, INT, SU leer. Von Hand nachtragen wuerde der naechste
      // Marktabgleich loeschen -- deshalb hier ein GRUND, kein Rueckfall auf
      // eine geratene Domain.
      : { art: 'fehlt', text: 'Customer page',
          grund: market_url ? 'no product handle' : `no market URL for ${marketLabel(market_code, 'this market')} on file` },
    // 🔴 W4 (Regel 16): der Satz sagt „would change DE" — bis hierher gab es
    // aber KEINEN Weg dorthin. Die Markt-ID kommt mit der Wurzel der Antwort
    // (`basis_market_id`), es braucht keinen neuen Endpunkt. Fehlt sie (alte
    // Serverfassung, kein DE-Markt), faellt das Ziel weg und der Satz bleibt
    // allein stehen — eine graue Zeile „no market" waere hier Rauschen.
    ...(istHinweis && sku && basis_market_id
      ? [{ art: 'intern', nach: `/products/${sku}/markets/${basis_market_id}`,
           text: `Change it in ${basisLabel}` }]
      : []),
  ];

  const hasContext = Boolean(reasons.length || competitor_shop || validUrl || hasScrape || range);
  const toggle = () => setExpanded((v) => !v);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderLeft: `${RAIL_WIDTH}px solid`, borderLeftColor: severityColor(severity) }}>
      {/* Zeile 1 -- Produkt, Markt, Alter | Preis */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: FS.lg, fontWeight: 600, lineHeight: 1.3 }} title={sku || undefined}>
            {product_name || sku}{variant_name ? ` · ${variant_name}` : ''}
          </Typography>
          <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
            {marketLabel(market_code, '—')}
            {alterText && (
              <Box component="span" sx={{ ml: 1, color: alterText.dringend ? 'warning.dark' : 'text.secondary',
                                          fontWeight: alterText.dringend ? 600 : 400 }}>
                {alterText.text}
              </Box>
            )}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: FS.sm, textDecoration: 'line-through', color: 'text.secondary' }}>
            {fmt(current_price, currency_code)}
          </Typography>
          <Typography sx={{ fontSize: FS.xl, fontWeight: 700, color: 'warning.main', lineHeight: 1.2 }}>
            {fmt(recommended_price, currency_code)}
          </Typography>
          {current_price > 0 && (
            <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
              {pct > 0 ? '+' : ''}{formatPercent(pct / 100, { digits: 1 })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Zeile 2 -- der EINE Grund */}
      {grundsatz && (
        <Typography sx={{ fontSize: FS.md, color: 'text.primary', mt: 0.75 }}>
          {grundsatz}
        </Typography>
      )}

      {/* Zeile 2b -- die Unterschreitung, immer sichtbar (O-B) */}
      {/* 🔴 NICHT `warning.main`. Gemessen 2026-09-18 (Regel-16-Gate, B2, und
          hier nachgerechnet): `#ed6c02` auf `#ffffff` bei FS.md = 13 px ergibt
          3,11:1 -- WCAG 2.1 AA (1.4.3) verlangt 4,5:1 unter 18,66 px. Eine
          Zeile, die es nur gibt, damit man sie OHNE Klick sieht, darf nicht die
          schlechtest lesbare der Karte sein. `warning.dark` loest es nicht
          (3,79:1, nachgerechnet).
          🔴 `error.main`, in BEIDEN Modi (seit 2026-10-04). Bis dahin
          fiel es DUNKEL durch (MUI-Standard `#f44336` auf `#1e293b` =
          3,97:1), und hier stand ein Modus-Zweig auf `error.light`. Seither
          bindet `theme/theme.js` das dunkle `error` an Owner-Entscheid E4
          (`V2.errTextDark`): dunkel 7,71:1, hell unveraendert
          `#d32f2f` 4,98:1. Der Zweig war damit gegenstandslos und ist
          zurueckgebaut. Der Dunkelmodus ist keine Randbedingung: er kommt
          ueber `prefers-color-scheme` UND ueber den Umschaltknopf in
          `SidebarAndHeaderBar.jsx` bzw. `MobileSidebar.jsx`, die Wahl liegt
          in `localStorage` (`pog-color-mode`).
          🟢 Damit ist auch der Preis geschlossen, den das Regel-16-Gate in
          Runde 2 benannt hatte: die Zeile traegt jetzt in BEIDEN Modi die
          Farbe des linken Kartenrands (`severityColor('high')` ->
          `error.main`; den Schweregrad `high` setzt
          `backend/pricing_automation.py` fuer genau diesen Ausloeser).
          🔴 Die vier Verhaeltnisse sind bewacht, nicht bloss aufgeschrieben:
          `PreisempfehlungPosten.test.jsx` rechnet sie in BEIDEN Modi am
          gerenderten Bild nach. Eine Prosa-Notiz zur Kontrastfrage auf `paper`
          ist auf dieser Flaeche schon einmal abgelehnt worden
          (`v2Bausteine.test.jsx`, Docstring der AA-Wache). */}
      {unterMindestpreis && (
        <Typography sx={{ fontSize: FS.md, mt: 0.25,
                          // ein Token fuer beide Modi, beide bestehen AA (s. oben)
                          color: 'error.main' }}>
          {unterMindestpreis}
        </Typography>
      )}

      {/* Zeile 2c -- der Hinweis (E6). 🔴 Eigene Zeile, NICHT in `grundsatz`
          verkettet: der Grund ist aus `reasonsOhneMindestpreis` gefiltert, damit
          er nicht zweimal dasteht (W6). */}
      {istHinweis && (
        <Typography id={hinweisId} sx={{ fontSize: FS.md, mt: 0.25, color: 'text.primary' }}>
          Follows the base price of {basisLabel} — accepting it here would change that market.
        </Typography>
      )}

      {/* Zeile 3 -- die drei Ziele, gleichrangig */}
      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        {ziele.map((z, i) => {
          if (z.art === 'fehlt') {
            return (
              <Typography key={i} component="span"
                sx={{ fontSize: FS.sm, color: 'text.secondary', fontStyle: 'italic' }}>
                {z.text} — {z.grund}
              </Typography>
            );
          }
          if (z.art === 'intern') {
            return (
              <Link key={i} component={RouterLink} to={z.nach}
                sx={{ fontSize: FS.sm, fontWeight: 600 }}>{z.text}</Link>
            );
          }
          return (
            <Link key={i} href={z.nach} target="_blank" rel="noopener noreferrer"
              sx={{ fontSize: FS.sm, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              {z.text}<OpenInNewIcon sx={{ fontSize: FS.sm }} />
            </Link>
          );
        })}
      </Box>

      {/* Zeile 4 -- Details + Entscheidung.
          🔴 E7 (2026-10-07): EINE Knopfhoehe fuer die ganze Karte — Theme
          `size="small"` plus Theme-Boden, KEINE `minHeight`/`fontSize`/`py`-
          Ueberschreibung mehr. Die Entscheidungszeile trug bis hierher flache
          44 px auf jeder Breite, waehrend „Accept All" daneben 28 px mass.
          Die Rangfolge ist Absicht: Reject (outlined error) · Later (text) ·
          Accept (contained success) — die annehmende Handlung ist die einzige
          gefuellte, und sie steht rechts aussen. */}
      <Box
        sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
      >
        {hasContext ? (
          <Button size="small" variant="text" onClick={toggle}
            aria-expanded={expanded}
            aria-describedby={istHinweis ? hinweisId : undefined}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ color: 'text.secondary' }}>
            Details
          </Button>
        ) : <Box />}
        {onDecide && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" color="error" disabled={deciding}
              aria-describedby={istHinweis ? hinweisId : undefined}
              onClick={() => onDecide('reject')}>
              Reject
            </Button>
            <Button size="small" variant="text" disabled={deciding}
              aria-describedby={istHinweis ? hinweisId : undefined}
              onClick={() => onDecide('defer')}
              sx={{ color: 'text.secondary' }}>
              Later
            </Button>
            {/* 🔴 E6: bei einem Hinweis wird „Accept" NICHT GERENDERT, nicht
                `disabled`. Ein grauer Knopf laedt zum Klicken ein und beantwortet
                den Klick nicht; ein fehlender Knopf sagt: hier gibt es nichts zu
                entscheiden. „Reject" bleibt — der Hinweis soll wegkoennnen. */}
            {!istHinweis && (
              <Button size="small" variant="contained" color="success" disabled={deciding}
                onClick={() => onDecide('approve')}>
                {deciding ? <CircularProgress size={14} color="inherit" /> : 'Accept'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {hasContext && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider',
                     display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {/* 🔴 `reasonsOhneMindestpreis`, nicht `reasons`. Gemessen vom
                Regel-16-Gate (W1, 2026-09-18): nach einem Klick standen sonst
                `Unter dem kalkulatorischen Mindestpreis (551,56 €)` und
                `Below calculated minimum (551.56)` gleichzeitig da -- derselbe
                Sachverhalt, zwei Sprachen, zwei Zahlformate. `551.56` liest
                sich im deutschen Umfeld wie ein Tausenderpunkt, es sahen also
                zwei VERSCHIEDENE Betraege aus. Genau die Doppelanzeige, wegen
                der der Owner die Verkettung abgelehnt hat -- nur eine
                Klickebene tiefer. Verloren geht nichts: die Zeile 2b traegt
                den Ausloeser ohne Klick.
                🟢 2026-10-07 (E7): die erste der beiden Zeichenketten ist
                uebersetzt (`Below the calculated minimum price (…)`); der
                Befund bleibt, nur die Sprache ist jetzt auf beiden Seiten
                dieselbe. */}
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
              Severity: {severity || 'unknown'}
              {reasonsOhneMindestpreis.length ? ` · ${reasonsOhneMindestpreis.join(' · ')}` : ''}
            </Typography>
            {hasScrape && (
              <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                Measurement: {scrape.is_fresh ? 'fresh' : 'stale'}
                {scrape.age_hours != null ? ` (${scrape.age_hours} h)` : ''}
                {scrape.reason ? ` · ${scrape.reason}` : ''}
              </Typography>
            )}
            {range && (
              <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                30 days: min {fmt(range.min, currency_code)}{hist.historical_mean != null ? ` · avg ${fmt(hist.historical_mean, currency_code)}` : ''} · max {fmt(range.max, currency_code)}
              </Typography>
            )}
            {sku && (
              <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>SKU {sku}</Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

// ── Run Results Preview Panel ─────────────────────────────────────────────────

/** Baut aus Backend-Warnungen die Meldung der Oberflaeche -- oder `null`.
 *
 * 🔴 EINE Stelle fuer Einzel- und Sammelweg. Die erste Fassung versah nur den
 * einzelnen "Accept"; "Accept All (n)" schrieb weiter lautlos unter den
 * Mindestpreis -- derselbe Vorfall, nur fuer viele Zeilen auf einen Klick, und
 * genau dort am teuersten. Vom Regel-16-Gate in der Wiedervorlage gefunden.
 *
 * Gibt bewusst `null` zurueck, wenn nichts zu melden ist: der Aufrufer setzt
 * den Zustand IMMER, damit keine alte Meldung ueber einer neuen Zeile stehen
 * bleibt.
 */
export function warnungenAusListe(warnungen) {
  const echte = (warnungen || []).filter(Boolean);
  if (echte.length === 0) return null;

  const geld = (w, v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return formatMoney(n, { currency: w.waehrung || 'EUR' });
  };
  // 🔴 EK-A15: es gibt ZWEI Mindestpreise, und bis zum 2026-09-15 nannte
  // diese Flaeche nur den durchgesetzten. Lag der kalkulatorische hoeher, stand
  // hier eine Zahl, die der Preis gar nicht riss -- oder die Zeile fehlte ganz.
  //
  // `produktboden` kann fehlen: alte Antwort waehrend eines Deploys, oder ein
  // Produkt ohne hinterlegten Wert. Dann bleibt der Satz, wie er war.
  const kopf = (w) => `${w.sku}${w.market_code ? ` · ${marketLabel(w.market_code)}` : ''}`;
  const satz = (w) => {
    // 🔴 Die Entscheidung wohnt in `utils/mindestpreisWarnung.js`.
    const l = bodenLage(w);
    // `kopf(w)` statt `w.sku`: diese Zeile verlor als einzige den Marktcode,
    // und in gemischter Liste wusste der Bediener nicht, WELCHER Markt
    // ungeprueft blieb.
    if (l.art === 'unbekannt') return `${kopf(w)}: the minimum price could not be checked.`;
    if (l.art === 'flagged') return `${kopf(w)}: ${geld(w, w.betrag)} was flagged${artZusatz(l)}.`;
    if (l.art === 'unter_produktboden') {
      // Drei Lagen, und keine davon darf eine Zahl nennen, die es nicht gibt.
      const kalk = l.zeigtProduktboden ? ` of ${geld(w, w.produktboden)}` : '';
      return l.zeigtMarktboden
        ? `${kopf(w)}: ${geld(w, w.betrag)} holds the market minimum of `
          + `${geld(w, w.mindestpreis)} but is below the calculated minimum${kalk}.`
        : `${kopf(w)}: ${geld(w, w.betrag)} is below the calculated minimum${kalk}.`;
    }
    return `${kopf(w)}: ${geld(w, w.betrag)} is below the minimum price`
      + (l.zeigtMarktboden ? ` of ${geld(w, w.mindestpreis)}` : '')
      + (l.zeigtProduktboden ? ` (calculated minimum ${geld(w, w.produktboden)})` : '')
      + `.`;
  };

  // 🔴 JE KLASSE ZAEHLEN, nicht die Gesamtzahl neben ein Praedikat setzen.
  //
  // Die erste Fassung nahm `echte.length` und die schaerfste vorkommende
  // Klasse. Bei einer gemischten Liste -- ueber "Alle annehmen" der
  // Normalfall (`handleAcceptAll`) -- stand dann "2 prices below the calculated
  // minimum", obwohl eine davon gar nicht geprueft werden konnte. Das ist
  // dieselbe Ungenauigkeit, gegen die diese Bahn angetreten ist, nur eine
  // Ebene hoeher. Vom Regel-16-Gate gefunden.
  const zaehle = (u) => echte.filter((w) => w.urteil === u).length;
  const n_unter = zaehle('unter');
  const n_produkt = zaehle('unter_produktboden');
  const n_unbekannt = zaehle('unbekannt');

  const teile = [];
  if (n_unter) teile.push(`${n_unter} below the minimum price`);
  if (n_produkt) teile.push(`${n_produkt} below the calculated minimum`);
  if (n_unbekannt) teile.push(`${n_unbekannt} could not be checked`);
  // Ein kuenftiges Urteil faellt nicht unter den Tisch: es taucht in der
  // Restzahl auf, statt einer falschen Klasse zugeschlagen zu werden.
  const rest = echte.length - n_unter - n_produkt - n_unbekannt;
  if (rest > 0) teile.push(`${rest} flagged`);

  const kopfzeile = `Applied — ${teile.join(', ')}`;

  return {
    anzahl: echte.length,
    zeilen: echte.map(satz),
    kopfzeile,
  };
}


function RunResultsPanel({ results, onApplyLive, applying, onDecide, decidingId, onOpenDetail }) {
  const [showApplied, setShowApplied] = useState(true);
  const [showReview, setShowReview] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const { stats, auto_applied = [], manual_review = [], errors = [] } = results;

  return (
    <Box>
      <Divider />
      <Box sx={{ p: 2, bgcolor: results.dry_run ? 'rgba(102,126,234,0.05)' : 'rgba(16,185,129,0.05)' }}>
        {results.dry_run ? (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <DisabledReason
                reason={applying
                  ? null
                  : stats.total_checked === 0 ? 'This run checked no prices — nothing to apply.' : null}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={applying ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                  disabled={applying || stats.total_checked === 0}
                  onClick={onApplyLive}
                >
                  {applying ? 'Applying…' : 'Apply live'}
                </Button>
              </DisabledReason>
            }
          >
            <strong>Preview only</strong> — no changes made yet. Review below, then click Apply live.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Run complete</strong> — {stats.auto_applied} price{stats.auto_applied !== 1 ? 's' : ''} updated,{' '}
            {stats.manual_review} queued for review.
          </Alert>
        )}

        {/* Stats row */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: results.dry_run ? 'Would auto-apply' : 'Auto-applied', value: stats.auto_applied, color: 'success.main' },
            { label: results.dry_run ? 'Would queue for review' : 'Queued for review', value: stats.manual_review, color: 'warning.main' },
            { label: 'Skipped', value: stats.skipped, color: 'text.secondary' },
            { label: 'Errors', value: stats.errors, color: 'error.main' },
          ].map(({ label, value, color }) => (
            <Grid size={{ xs: 6, sm: 3 }} key={label}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Auto-applied / Would auto-apply */}
        {auto_applied.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <SectionToggle
              icon={<CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />}
              label={results.dry_run ? 'Would auto-apply' : 'Auto-applied'}
              count={auto_applied.length}
              expanded={showApplied}
              onToggle={() => setShowApplied((v) => !v)}
            />
            <Collapse in={showApplied}>
              <Stack spacing={0.75}>
                {auto_applied.map((item, i) => (
                  <PriceChange
                    key={i}
                    {...item}
                    shopify_pushed={item.shopify_pushed}
                    shopify_error={item.shopify_error}
                    has_variant_id={item.has_variant_id}
                    has_price_list_id={item.has_price_list_id}
                    onClick={onOpenDetail ? () => onOpenDetail(item) : undefined}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {/* Would queue for review (dry run only) */}
        {manual_review.length > 0 && results.dry_run && (
          <Box sx={{ mb: 1.5 }}>
            <SectionToggle
              icon={<WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />}
              label="Would queue for review"
              count={manual_review.length}
              expanded={showReview}
              onToggle={() => setShowReview((v) => !v)}
            />
            <Collapse in={showReview}>
              <Stack spacing={0.75}>
                {manual_review.map((item, i) => (
                  <ReviewItem
                    key={i}
                    {...item}
                    current_price={item.old_price ?? item.current_price}
                    recommended_price={item.new_price ?? item.recommended_price}
                    triggers={item.triggers ?? item.review_reasons}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
        {manual_review.length > 0 && !results.dry_run && (() => {
          // 🔴 R16-W8: `stats.hints` liegt serverseitig vor (`pricing_automation`).
          // Bis hierher schickte der Satz den Nutzer zum Annehmen von Zeilen,
          // die sich nicht annehmen lassen — bei lauter Hinweisen war der
          // einzige Satz ueber dem Ergebnis falsch.
          const hinweiseImLauf = Math.min(Number(stats?.hints) || 0, manual_review.length);
          const freigebbarImLauf = manual_review.length - hinweiseImLauf;
          // 🔴 R16 „Singular": bei GENAU einem Hinweis stand „1 item queued for
          // review — all of them follow …". „all of them" ueber einer Zeile ist
          // falsch; die Einzahl bekommt ihr eigenes Pronomen.
          const satz = hinweiseImLauf === 0
            ? ' — see Pending Review Queue below to accept or reject.'
            : freigebbarImLauf === 0
              ? (manual_review.length === 1
                  ? ` — it follows the ${BASIS_ETIKETT} base price and cannot be accepted here.`
                  : ` — all of them follow the ${BASIS_ETIKETT} base price and cannot be accepted here.`)
              : ` — ${hinweiseImLauf} follow${hinweiseImLauf === 1 ? 's' : ''} the ${BASIS_ETIKETT} base price and cannot be accepted; the rest can be accepted or rejected below.`;
          return (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{ mb: 1.5, py: 0.75 }}
            >
              <strong>{manual_review.length} item{manual_review.length !== 1 ? 's' : ''} queued for review</strong>
              {satz}
            </Alert>
          );
        })()}

        {/* Errors */}
        {errors.length > 0 && (
          <Box>
            <SectionToggle
              icon={<ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />}
              label="Errors"
              count={errors.length}
              expanded={showErrors}
              onToggle={() => setShowErrors((v) => !v)}
            />
            <Collapse in={showErrors}>
              <Stack spacing={0.75}>
                {errors.map((err, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, borderLeftColor: 'error.main', borderLeft: `${RAIL_WIDTH}px solid` }}>
                    <Typography variant="caption" fontWeight={600}>{err.sku} / {marketLabel(err.market_code, '—')}</Typography>
                    <Typography variant="caption" color="error" display="block">{err.error}</Typography>
                  </Paper>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {stats.total_checked === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
            No actionable price recommendations found right now.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Last Automated Run Panel ──────────────────────────────────────────────────

function LastRunPanel({ onOpenDetail }) {
  const [ladeFehler, setLadeFehler] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // 🔴 Der Rumpf des Ladeeffekts steht seit Nachbesserung 2 in einer benannten
  // Funktion (E-8, 2026-10-11) — der bestehende Ladeweg, kein zweiter.
  const laden = useCallback(() => {
    // 🔴 Der Fehlerzustand wird beim NEULADEN zurueckgesetzt (E-8). Genau
    // hierauf lief Z1.8 auf: der Alarm „The last run could not be loaded."
    // stand nach der Erholung weiter im Baum, weil dieser Lader nur beim
    // Einhaengen fuhr und `ladeFehler` niemand zuruecknahm.
    setLadeFehler(false);
    setLoading(true);
    fetch(`${API}/api/pricing/automation/last-run`)
      // 🔴 `holeJson` statt `(r.ok ? r.json() : null)` (Z-3): ein 502 loeste zu
      // `null` auf, `ladeFehler` blieb `false` und der Fehlerzweig unten lief
      // nie — die Karte meldete „kein Lauf" statt „nicht geladen".
      .then(holeJson)
      .then((d) => { if (d) setData(d); })
      .catch(() => setLadeFehler(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // 🔴 UI2 Teil 9, Karte Z-1c, E-8 (Nachbesserung 2, 2026-10-11): nach der
  // Erholung laedt die Karte selbst neu. Sie faehrt ihren Abruf nur beim
  // Einhaengen (`useEffect(…, [])`), haengt an keinem Hook und blieb deshalb im
  // Fehler stehen — das war die vierte Form des Owner-Fotos.
  useReloadOnRecovery(laden);

  // 🔴 Vor dem Ladezweig: ein Fehlschlag ist kein Ladevorgang und keine
  // Leere. Der stille `.catch` liess ihn bis zum 2026-09-17 wie "kein Lauf
  // vorhanden" aussehen.
  if (ladeFehler) return (
    <>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ fontSize: FS.md }}>
          The last run could not be loaded.
        </Alert>
      </Box>
    </>
  );

  if (loading) return (
    <>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">Loading last run…</Typography>
      </Box>
    </>
  );

  if (!data || !data.run_at) return null;

  const { run_at, changes, stats } = data;
  const hasIssues = stats.shopify_failed > 0 || stats.missing_ids > 0;

  return (
    <>
      <Divider />
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AutoModeIcon fontSize="small" sx={{ color: V2.infoText }} />
            Last Automated Run
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
              {formatDateTime(run_at)}
            </Typography>
          </Typography>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} last automated run`}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={`${stats.total} changed`}
            size="small"
            sx={{ height: 20, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.success.main, 0.1), color: 'success.dark' }}
          />
          <Chip
            icon={<CloudDoneIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={`${stats.shopify_pushed} Shopify ✓`}
            size="small"
            sx={{ height: 20, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.info.main, 0.1), color: 'info.dark' }}
          />
          {stats.missing_ids > 0 && (
            <Chip
              icon={<HelpOutlineIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={`${stats.missing_ids} missing IDs`}
              size="small"
              sx={{ height: 20, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.warning.main, 0.1), color: 'warning.dark' }}
            />
          )}
          {stats.shopify_failed > 0 && (
            <Chip
              icon={<CloudOffIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={`${stats.shopify_failed} Shopify ✗`}
              size="small"
              sx={{ height: 20, fontSize: FS.xxs, bgcolor: t => alpha(t.palette.error.main, 0.12), color: 'error.dark' }}
            />
          )}
        </Box>

        {hasIssues && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: FS.sm }}>
            {stats.missing_ids > 0 && (
              <span><strong>{stats.missing_ids} item{stats.missing_ids !== 1 ? 's' : ''}</strong> were NOT synced to Shopify — missing variant_id or price_list_id. Click a row for details. </span>
            )}
            {stats.shopify_failed > 0 && (
              <span><strong>{stats.shopify_failed} item{stats.shopify_failed !== 1 ? 's' : ''}</strong> had a Shopify API error. </span>
            )}
          </Alert>
        )}

        <Collapse in={expanded}>
          {changes.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No changes in last run.</Typography>
          ) : (
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {changes.map((c) => (
                <PriceChange
                  key={c.id}
                  {...c}
                  onClick={() => onOpenDetail(c)}
                />
              ))}
            </Stack>
          )}
        </Collapse>
      </Box>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function PricingAutomationDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [reviewQueue, setReviewQueue] = useState([]);
  // 🔴 W3: die Erfolgsmeldung des Sammelwegs. Sie steht getrennt von
  // `entscheidFehler`, weil sie kein Fehler ist — ein rotes Alert ueber einem
  // gelungenen Vorgang waere die falsche Farbe.
  const [sammelMeldung, setSammelMeldung] = useState(null);
  const [showReviewQueue, setShowReviewQueue] = useState(true);
  const [acceptedChanges, setAcceptedChanges] = useState([]);
  // 🔴 Eingeklappt per Vorgabe. Owner-Entscheid 2026-09-17: die Sektion
  // vermischte zwei Anzeigen -- ein Betriebsprotokoll (was lief, was ging
  // nach Shopify) und eine Entscheidungsflaeche (nehme ich diesen Preis?).
  // Beide trugen dieselbe Dichte, und die Entscheidungsflaeche verlor.
  // Oben steht jetzt nur noch, was auf eine Entscheidung wartet; das
  // Protokoll ist einen Klick entfernt, nicht weg.
  const [showAccepted, setShowAccepted] = useState(false);

  // Run state
  const [runStatus, setRunStatus] = useState(null); // null | 'running' | 'done' | 'error'
  const [runResults, setRunResults] = useState(null);
  const [runError, setRunError] = useState(null);
  const [runIsDryRun, setRunIsDryRun] = useState(false);
  const [applying, setApplying] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  // Der sichtbare Fehlschlag eines Einzelentscheids (u.a. der 409).
  const [entscheidFehler, setEntscheidFehler] = useState(null);

  // Detail modal (single change)
  const [detailChange, setDetailChange] = useState(null);

  // Mindestpreis-Warnung nach einem Accept oder Accept All. 🔴 KEIN Toast: das ist eine
  // Geldmeldung zu einem Vorgang, der nicht zurueckgeholt werden kann, und ein
  // Toast ist nach vier bis acht Sekunden weg. Sie bleibt stehen, bis die
  // Person sie schliesst.
  const [sperre, setSperre] = useState(null);

  // Stat detail modal (KPI click)
  const [statModal, setStatModal] = useState(null); // { title, subtitle, fetchUrl?, items?, itemType? }
  const openStatModal = (cfg) => setStatModal(cfg);
  const closeStatModal = () => setStatModal(null);

  // Map a pending review-queue item to a PriceChange-shaped row for the Accepted
  // section (optimistic, before the server-side price_change_log row is fetched).
  const reviewItemToChange = (item) => ({
    id: `optim-${item.id}`,
    sku: item.sku,
    product_name: item.product_name,
    variant_name: item.variant_name,
    market_code: item.market_code,
    old_price: item.current_price,
    new_price: item.recommended_price,
    currency_code: item.currency_code,
    source: 'manual_review_approved',
    competitor_shop: item.competitor_shop,
    competitor_price: item.competitor_price,
    changed_at: new Date().toISOString(),
    shopify_pushed: undefined,
  });

  // 🔴 Der Ladeweg dieser Karte steht seit Nachbesserung 2 als `useCallback`
  // (E-8, 2026-10-11) — `useReloadOnRecovery` haengt an DIESER Funktion, es
  // kommt kein zweiter Abruf dazu.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, queueRes, acceptedRes] = await Promise.all([
        // 🔴 Z-3: `.then(holeJson)` statt `(r.ok ? r.json() : null)` — sonst
        // loest ein 502 zu `null` auf, `if (summaryRes)` ueberspringt still,
        // und der Fangzweig darunter ist unerreichbar.
        //
        // 🔴 W2 (Regel-16-Runde 1, 2026-10-10): `Promise.all` bleibt ABSICHTLICH
        // und wird NICHT in drei eigene Zweige getrennt. Ein Ausfall EINES der
        // drei Abrufe leert damit auch die vier Kennzahlen — vorher blieben sie
        // bei einem Ausfall der Warteschlange richtig. Die neue Form ist die
        // bessere: ein Teilausfall ist UNBEKANNT, nicht eine halbe Wahrheit.
        // Getrennt gefangen zeigten die Kacheln weiter Zahlen aus einer Sicht,
        // die gerade nicht vollstaendig ist — genau die Sorte „Zahl ohne
        // Messung", die diese Karte abstellt (Regel 22 §„Unknown is never 0").
        fetch(`${API}/api/pricing/automation/summary`).then(holeJson),
        fetch(`${API}/api/pricing/automation/review-queue?limit=20`).then(holeJson),
        fetch(`${API}/api/pricing/automation/recent-changes?source=manual_review_approved&since_hours=24&limit=50`).then(holeJson),
      ]);
      if (summaryRes) setSummary(summaryRes);
      if (queueRes) setReviewQueue(queueRes.items || []);
      // Full-array replacement (NOT key-merge) is what drops the optimistic `optim-`
      // rows: the server rows fully replace them. A future incremental-merge refactor
      // must add sku+market_code dedupe to avoid duplicating an accepted row.
      if (acceptedRes) setAcceptedChanges(acceptedRes.changes || []);
      // 🔴 Der Fehlerzustand wird beim Neuladen zurueckgesetzt (E-8,
      // Nachbesserung 2) — hier, im ERFOLGSZWEIG: nur ein gelungener Abruf
      // raeumt die Meldung weg. Scheitert er, setzt der Fangzweig darunter
      // seinen eigenen Satz — aber nur, solange keine Handlungsmeldung steht
      // (R15-W1, Nachbesserung 4). Ohne diese Zeile stuende „The pricing data
      // could not be loaded. Please reload." nach der Erholung weiter im Baum,
      // obwohl die Karte ihre Daten laengst wieder hat.
      //
      // 🔴 R16-B-1 (Nachbesserung 3, 2026-10-11): geraeumt wird NUR die
      // LADEMELDUNG — die, die dieser Abruf im Fangzweig selbst setzt und die
      // `laden: true` traegt. Ein blankes `setEntscheidFehler(null)` loeschte
      // auch die HANDLUNGSMELDUNGEN, die eine andere Stelle derselben Datei
      // unmittelbar davor setzt: den Sammellauf-Satz („N of M … NOT applied"),
      // „There was nothing left to accept", den 409-Satz und den
      // Transportfehler. `handleAcceptAll` setzt seinen Satz und ruft DANACH
      // `fetchData()`; in der Produktion loesen die drei Abrufe auf, die
      // Meldung stand also nur fuer die Dauer der Netzlaufzeit im Baum — und
      // danach behauptete die Oberflaeche, alles sei angewandt worden, obwohl
      // Zeilen ausdruecklich NICHT angewandt wurden.
      //
      // 🔴 Die FORM allein trennt nicht: „There was nothing left to accept"
      // traegt wie die Lademeldung `{ id: null, neuLaden: true }`. Das Merkmal
      // ist deshalb `laden`, und nur die Lademeldung setzt es.
      setEntscheidFehler((f) => (f && f.laden ? null : f));
    } catch (err) {
      // 🔴 Der wichtigste der drei Abrufe -- er laedt Zusammenfassung,
      // Warteschlange und "Angenommen".
      //
      // 🟢 BEHOBEN am 2026-10-10 (Z-3, UI2 Teil 9). Hier stand bis dahin eine
      // Praezisierung vom 2026-09-18, die festhielt, dass ein HTTP 500 NICHT
      // hierher gelangt: alle drei Abrufe lauteten
      // `.then((r) => (r.ok ? r.json() : null))`, ein 500 loeste zu `null` auf,
      // warf NICHT, `if (summaryRes)` uebersprang still — dieser Fangzweig
      // feuerte ausschliesslich bei Transport- und JSON-Parsefehlern. Der
      // stille 500er stand als Z13 im Backlog
      // (`docs/backlog-auto-repair/2026-09-18-preisempfehlung-offene-zettel.md:188`).
      // Die drei Abrufe laufen jetzt ueber `holeJson` und werfen bei `!r.ok`;
      // damit traegt dieser Zweig den 502er. Vom Regel-15-Gate am 2026-09-18
      // gemessen, am 2026-10-10 gebaut.
      //
      // 🔴 `neuLaden` ist NICHT `veraltet`. `veraltet` faerbt den Alert
      // gelb (eine abgeloeste Zeile ist kein Fehler); ein gescheiterter
      // Abruf IST einer und bleibt rot. Beide brauchen aber denselben
      // Knopf. Bis zum 2026-09-18 hing die Aktion allein an `veraltet` --
      // also sagte ausgerechnet DIESE Meldung "Bitte neu laden." und bot
      // als einzige keinen Knopf dafuer an. Vom Regel-16-Gate gemessen.
      // 🟢 2026-10-07 (E7): der Satz ist uebersetzt; das Zitat oben steht
      // als Aufzeichnung des damaligen Wortlauts.
      // 🔴 R15-W1 (Nachbesserung 4, 2026-10-11): die Lademeldung wird nur
      // gesetzt, wenn KEINE Nicht-Lade-Meldung steht. Sonst ueberschriebe ein
      // GESCHEITERTER Nachlauf-Abruf genau den Satz, den `handleAcceptAll`
      // unmittelbar davor gesetzt und mit `fetchData()` nachgezogen hat —
      // dieselbe Klasse wie R16-B-1, einen Zweig weiter. Die Lademeldung ist
      // die schwaechste der Karte: sie sagt nur, dass nichts geladen wurde;
      // der Sammellauf-Satz sagt, dass eine Preisaenderung NICHT geschrieben
      // wurde. Die schwerere bleibt stehen.
      setEntscheidFehler((f) => {
        if (f && !f.laden) return f;
        return {
          id: null,
          laden: true,   // 🔴 R16-B-1: nur DIESE Meldung raeumt der Erfolgszweig oben
          text: 'The pricing data could not be loaded. Please reload.',
          veraltet: false,
          neuLaden: true,
        };
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerRun = async (dry_run) => {
    setRunStatus('running');
    setRunIsDryRun(dry_run);
    setRunResults(null);
    setRunError(null);
    try {
      const res = await fetch(`${API}/api/pricing/automation/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run }),
      });
      let data = null;
      try { data = await res.json() } catch { /* non-JSON body */ }
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status} ${res.statusText || ''}`.trim());
      }
      setRunResults(data);
      setRunStatus('done');
      if (!dry_run) fetchData();
    } catch (err) {
      setRunStatus('error');
      setRunError(err?.message || String(err) || 'Run failed');
      console.error('Automation run failed:', err);
    }
  };

  const handleApplyLive = async () => {
    setApplying(true);
    await triggerRun(false);
    setApplying(false);
  };

  const handleDecision = async (reviewId, action) => {
    setDecidingId(reviewId);
    try {
      const res = await fetch(`${API}/api/pricing/automation/review/${reviewId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        // 🔴 Bis zum 2026-09-17 stand hier `console.error(...); return;` --
        // ein Klick, bei dem nichts passiert und nichts erklaert wird.
        //
        // Das wird jetzt der REGELFALL, nicht der Randfall: die Automatik legt
        // je Lauf eine neue Zeile an und schliesst die vorherige, die `id`
        // wechselt also zweimal taeglich -- und diese Ansicht frischt sich
        // nicht selbst auf (`useEffect(..., [])`). Ein offener Tab haelt seine
        // `id`s unbegrenzt. Das Backend liefert dafuer bewusst einen 409 mit
        // deutscher `message`; sie gehoert auf den Schirm.
        setEntscheidFehler({
          id: reviewId,
          // 🔴 `errData.error` steht NICHT mehr in der Anzeigekette.
          //
          // Gemessen am 2026-09-18 (Regel-16-Gate): von fuenf Fehlerlagen
          // dieses Endpunkts tragen VIER ausschliesslich `error` und keine
          // `message` -- darunter der 500er, der `str(e)` zurueckgibt. Der
          // Satz mit dem naechsten Schritt wurde also fast nie erreicht,
          // und stattdessen stand ein roher Python-Ausnahmetext in einer
          // sonst englischen Flaeche. `error` ist ein Code fuer Maschinen;
          // er gehoert ins Protokoll, nicht auf den Schirm.
          text: errData.message
            || `The decision could not be saved (${res.status}). Please try again.`,
          veraltet: res.status === 409,
          // 🔴 Auch beim 5xx. `_apply_price_to_shopify_and_db` committet
          // INTERN, nach dem Push -- ein 500er danach laesst den Preis live
          // und den Anspruch gesetzt zurueck. Dieselbe Lage wie beim
          // Transportfehler: die Liste ist von unbekannter Gueltigkeit.
          // Vom Regel-15-Gate (Runde 6) gemessen.
          neuLaden: res.status === 409 || res.status >= 500,
        });
        if (errData.error) console.error('[pricing] review decision failed:', errData.error);
        return;
      }
      setEntscheidFehler(null);

      // 🔴 Der "Accept"-Knopf schrieb bis hierher LAUTLOS unter den Mindestpreis.
      //
      // Vom Regel-16-Gate als B3 gemessen: vor dem Owner-Entscheid lief ein
      // Accept auf eine Unterschreitung in einen verschluckten 500 -- die Zeile
      // blieb stehen, der Preis wurde nicht geschrieben. Seit dem Entscheid wird
      // er geschrieben und die Zeile verschwindet; ohne diese Meldung ohne ein
      // Wort.
      //
      // ⚠️ Das `below_min_price`-Merkmal in der Pruefliste deckt das NICHT ab:
      // es misst `min_price` (Produktboden), waehrend durchgesetzt wird
      // `COALESCE(mp.min_price, p.min_price)` -- der Marktboden zuerst. Zwei
      // verschiedene Zahlen, und die Pruefliste zeigt die andere.
      const daten = await res.json().catch(() => ({}));
      // 🔴 IMMER setzen, auch auf `null`. Die Vorfassung setzte den Zustand nur
      // im Warnfall und raeumte ihn nie: ein Accept auf SKU A zeigte A's Zahlen,
      // und ein folgendes Accept auf SKU B OHNE Unterschreitung liess die alte
      // Meldung stehen -- fremde Zahlen ueber einer neuen Zeile. Das ist
      // schlechter als gar keine Meldung, weil es aussieht wie eine Aussage
      // ueber die gerade abgeschlossene Handlung. Vom Regel-16-Gate gefunden.
      setSperre(warnungenAusListe([daten.mindestpreis_warnung]));
      // Optimistically move an approved item into the Accepted section so it does
      // not vanish; fetchData() then reconciles with the price_change_log record.
      if (action === 'approve') {
        const accepted = reviewQueue.find((i) => i.id === reviewId);
        if (accepted) setAcceptedChanges((a) => [reviewItemToChange(accepted), ...a]);
      }
      setReviewQueue((q) => q.filter((i) => i.id !== reviewId));
      setRunResults((r) => r ? {
        ...r,
        manual_review: (r.manual_review || []).filter((i) => i.review_id !== reviewId),
        stats: { ...r.stats, manual_review: Math.max(0, (r.stats?.manual_review ?? 1) - 1) },
      } : r);
      fetchData();
    } catch (err) {
      // 🔴 Transportfehler (Netz weg, Server nicht erreichbar) wurden bis
      // hierher nur auf die Konsole geschrieben -- HTTP-Fehler zeigte die
      // Oberflaeche, Transportfehler nicht. Vom Regel-15-Gate gefunden.
      setEntscheidFehler({
        id: null,
        text: 'The decision could not reach the server. Please try again.',
        veraltet: false,
        // 🔴 Ein Transportfehler heisst NICHT "nichts ist passiert": der
        // Server kann die Anfrage ausgefuehrt haben, nur die Antwort kam nie
        // an. Die Liste auf dem Schirm ist danach von nachweislich
        // unbekannter Gueltigkeit -- genau der Zustand, in dem Neuladen am
        // dringendsten ist. Vom Regel-15-Gate gemessen.
        neuLaden: true,
      });
    } finally {
      setDecidingId(null);
    }
  };

  const [acceptingAll, setAcceptingAll] = useState(false);
  const handleAcceptAll = async () => {
    setAcceptingAll(true);
    // W3: die Meldung des VORIGEN Sammellaufs gehoert nicht zum neuen.
    setSammelMeldung(null);
    try {
      const res = await fetch(`${API}/api/pricing/automation/review/accept-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setEntscheidFehler({
          id: null,
          // Dieselbe Kette wie oben, derselbe Grund: der 500er dieses
          // Endpunkts gibt ebenfalls `{"error": str(e)}` zurueck.
          text: errData.message
            || `The bulk action failed (${res.status}). Please try again.`,
          // 🔴 KEIN `res.status === 409` mehr: dieser Endpunkt antwortet 200
          // oder 500, nie 409 -- die Bedingung war tot und haette den Knopf
          // genau nie gezeigt. Vom Regel-15-Gate gemessen.
          veraltet: false,
          neuLaden: true,
        });
        if (errData.error) console.error('[pricing] accept-all failed:', errData.error);
        return;
      }
      setEntscheidFehler(null);
      // 🔴 Auch der Sammelweg meldet. Bis hierher schrieb "Accept All (n)"
      // lautlos unter den Mindestpreis -- derselbe Vorfall wie beim einzelnen
      // Accept, nur fuer n Zeilen auf einen Klick.
      const daten = await res.json().catch(() => ({}));
      setSperre(warnungenAusListe(
        (daten.results || []).map((e) => e.mindestpreis_warnung)));

      // 🔴 R16-W3: der Server sagt, wie viele Hinweise er uebergangen hat
      // (`skipped_hints`). Bis hierher kam die Zahl an und wurde NIE gezeigt —
      // nach einem Klick war die einzige Rueckmeldung „N of M", ohne ein Wort
      // dazu, dass Zeilen absichtlich liegen blieben. Plan E4 begruendet die
      // Zahl ausdruecklich: ohne sie ist „uebersprungen" von „nicht gelesen"
      // nicht zu unterscheiden.
      const uebersprungen = Number.isFinite(Number(daten.skipped_hints))
        ? Number(daten.skipped_hints) : 0;
      const hinweisSatz = uebersprungen > 0
        ? ` ${uebersprungen} hint${uebersprungen === 1 ? ' was' : 's were'} skipped — `
          + `${uebersprungen === 1 ? 'it follows' : 'they follow'} the ${BASIS_ETIKETT} base price.`
        : '';

      // 🔴 JE ZEILE auswerten. Bis zum 2026-09-17 schob diese Stelle die GANZE
      // Warteschlange nach "Accepted", unabhaengig davon, was der Server je
      // Zeile geantwortet hatte -- eine fehlgeschlagene Zeile erschien dem
      // Owner als angewandter Preis. Das ist der Befund mit dem groessten
      // Geldbezug: falsche Gewissheit ueber live geschriebene Preise.
      //
      // Der Server liefert `results: [{id, ok, error}]`. Zeilen ohne `ok`
      // bleiben stehen und werden benannt.
      const ergebnisse = daten.results || [];
      const gelungen = new Set(
        ergebnisse.filter((e) => e && e.ok !== false).map((e) => e.id));
      const gescheitert = ergebnisse.filter((e) => e && e.ok === false);

      // 🔴 Ein LEERES `results` heisst NICHT "aeltere Serverfassung".
      //
      // Die erste Fassung dieses Zweigs nahm das an und schob dann die GANZE
      // Client-Warteschlange nach "Angenommen" -- womit sie genau den Befund
      // wieder aufmachte, gegen den sie gebaut wurde. Vom Regel-16-Gate
      // gemessen: `git show origin/master:backend/pricing_api.py:1845` gibt
      // bereits `{"results": results}` zurueck. Eine Fassung ohne
      // Zeilenergebnisse GIBT ES NICHT.
      //
      // Leer heisst: NULL Zeilen verarbeitet (serverseitig schon leer, alles
      // abgeloest). Dann wird nichts uebernommen.
      const uebernommen = reviewQueue.filter((z) => gelungen.has(z.id));
      const geblieben = reviewQueue.filter((z) => !gelungen.has(z.id));

      if (gescheitert.length) {
        // 🔴 KEINE Ortsangabe mehr. Bis zum 2026-09-18 stand hier "Sie stehen
        // weiterhin oben." -- und das kann falsch werden: hat ein anderer
        // Schreiber die Zeile beansprucht (genau der Fall, den
        // `stale_review_item` meldet), schreibt der eine `price_change_log`
        // -Zeile mit `manual_review_approved`, und das direkt folgende
        // `fetchData()` zieht dieselbe Zeile nach "Accepted". Die Meldung
        // zeigte dann auf eine Liste, in der sie nicht mehr steht. Vom
        // Regel-16-Gate gemessen. Der neue Wortlaut sagt, was DIESER Vorgang
        // getan hat -- das kann kein spaeterer Abruf widerlegen.
        // 🟢 2026-10-07 (E7): uebersetzt; die zitierte alte Fassung oben steht
        // als Aufzeichnung, die Begruendung gilt unveraendert.
        setEntscheidFehler({
          id: null,
          text: `${gescheitert.length} of ${ergebnisse.length} recommendations were `
              + `NOT applied by this action `
              + `(${gescheitert.some((e) => e.error === 'stale_review_item')
                    ? 'no longer current' : 'error'}). The list below is updated.`
              + hinweisSatz,
          veraltet: gescheitert.some((e) => e.error === 'stale_review_item'),
          neuLaden: true,
        });
      } else if (!ergebnisse.length) {
        // 🔴 Der stumme Ausgang. Ein Klick auf "Accept All (n)", bei dem der
        // Server NULL Zeilen verarbeitet hat, aenderte bis hierher gar nichts
        // auf dem Schirm -- keine Meldung, keine Bewegung, keine Erklaerung.
        // Nicht falsch, aber unbelegt: der Benutzer kann "hat nichts getan"
        // nicht von "hat nicht funktioniert" unterscheiden. Vom Regel-16-Gate
        // gemessen (W2).
        //
        // 🔴 R15-W1 (Nachbesserung 5, 2026-10-11): der Satz behauptet die
        // Aktualisierung NICHT mehr. Bis hierher hiess er „… — the list is
        // updated." und wurde damit nach einem GESCHEITERTEN Nachlauf-Abruf
        // falsch: `fetchData` laesst eine stehende Handlungsmeldung seit
        // R15-W1 (Nachbesserung 4) stehen, dieser Satz bleibt also im Baum,
        // waehrend die Liste gerade NICHT nachgezogen wurde. Der neue Wortlaut
        // sagt nur, was DIESER Vorgang getan hat — dieselbe Regel wie beim
        // Sammellauf-Satz darueber („das kann kein spaeterer Abruf
        // widerlegen"). Der Reload-Knopf bleibt ueber `neuLaden: true`.
        setEntscheidFehler({
          id: null,
          text: `There was nothing left to accept.${hinweisSatz}`,
          veraltet: true,
          neuLaden: true,
        });
      }

      if (!gescheitert.length && ergebnisse.length && hinweisSatz) {
        // Eigene Meldung, kein Fehler-Alert: der Vorgang ist gelungen, und ein
        // rotes Feld ueber einem gelungenen Vorgang waere die falsche Farbe.
        setSammelMeldung(
          `${gelungen.size} of ${ergebnisse.length} recommendations applied.${hinweisSatz}`);
      }

      setAcceptedChanges((a) => [...uebernommen.map(reviewItemToChange), ...a]);
      setReviewQueue(geblieben);
      setRunResults((r) => r ? { ...r, manual_review: [], stats: { ...r.stats, manual_review: 0 } } : r);
      fetchData();
    } catch (err) {
      // 🔴 Transportfehler (Netz weg, Server nicht erreichbar) wurden bis
      // hierher nur auf die Konsole geschrieben -- HTTP-Fehler zeigte die
      // Oberflaeche, Transportfehler nicht. Vom Regel-15-Gate gefunden.
      setEntscheidFehler({
        id: null,
        text: 'The bulk action could not reach the server. Please try again.',
        veraltet: false,
        // Dasselbe, und hier schwerer: der Sammelweg kann einen TEIL der
        // Zeilen geschrieben haben, bevor die Verbindung abriss.
        neuLaden: true,
      });
    } finally {
      setAcceptingAll(false);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  // 🔴 UI2 Teil 9, Karte Z-1c, E-8 (Nachbesserung 2, 2026-10-11): nach der
  // Erholung laedt auch der Kartengrund neu — ueber `fetchData`, ihren
  // bestehenden Ladeweg (kein zweiter). Der Abruf faehrt sonst nur beim
  // Einhaengen und blieb im Fehler stehen; die Kennzahlen standen dann nach der
  // Erholung weiter auf „—".
  useReloadOnRecovery(fetchData);

  const summaryData = summary || {
    last_run_at: null,
    total_changes_24h: 0,
    auto_applied_24h: 0,
    manual_review_pending: 0,
    manual_review_hints: 0,
    errors_24h: 0,
  };

  // 🔴 Z-3 / Regel 22 §„Unknown is never 0" (2026-10-10). Die Vorgabezeile
  // darueber ist die STRUKTUR (sie haelt das Rendern der uebrigen Abschnitte
  // zusammen) — sie ist aber keine Aussage. Solange `summary` nicht geladen
  // ist, sind die vier Kennzahlen UNBEKANNT, nicht null: vorher zeichnete ein
  // 502 vier echte Nullen und „Last run: Never" (Foto des Owners). `—` ist die
  // Hausform fuer unbekannt (`StatTile` selbst rendert `value ?? '—'`,
  // `SourceManagerPage.jsx:660`). Der Fehlertext selbst kommt aus dem Alert
  // unter den Kacheln.
  const kpi = (n) => (summary ? n : '—');

  // 🔴 W1 (Regel-16-Runde 1, 2026-10-10): dieselbe Bedingung fuer die FARBE.
  // `kpi` setzt nur den TEXT auf `—`; ohne diese Zeile trugen zwei der vier
  // Kacheln weiter ihre SEMANTISCHE Farbe und behaupteten damit eine Aussage
  // ueber einen Wert, den niemand gemessen hat: `valueColor="success.main"`
  // (Auto-Applied, unbedingt) und `errors_24h > 0 ? 'error.main' :
  // 'success.main'` (Errors — bei ungeladener Zusammenfassung ist `errors_24h`
  // `0`, also GRUEN). „Errors (24h): —" in Gruen liest sich als „keine Fehler".
  // Das ist die Regel-22-Klasse „Unknown is never 0", eine Ebene weiter auf dem
  // Farbkanal. Ohne `valueColor` setzt `StatTile` `text.primary`
  // (`StatTile.jsx:328`) — genau die neutrale Farbe, die ein unbekannter Wert
  // traegt.
  const kpiFarbe = (farbe) => (summary ? farbe : undefined);

  // 🔴 E6/E7: zwei Zahlen, zwei Bedeutungen — und seit der Gate-Nachbesserung
  // EINE Quelle fuer beide (R16-B1/W1/W2, R15-W1/W2).
  //   `freigebbarGesamt` — Zeilen, die jemand annehmen KANN (Markt mit eigener
  //                        Liste oder der Basis-Markt). Das ist die Zahl am
  //                        Knopf UND sein `disabled`: der Sammelweg hat kein
  //                        LIMIT, er nimmt genau diese Menge.
  //   `hinweiseGesamt`   — Zeilen, die den Basispreis erben. Offen, aber nicht
  //                        freigebbar; sie stehen als eigener Zusatz am Kopf.
  // Beide kommen vom SERVER und zaehlen die ganze Datenbank. Aus der Liste
  // abgeleitet waren sie eine Untergrenze: `?limit=20` schneidet ab, und
  // standen die ersten zwanzig Zeilen alle auf „Hinweis", zeigte der Knopf
  // „Accept All (0)" ueber Zeilen, die der Server sehr wohl angenommen haette.
  // `null` heisst „noch nicht gemessen" (Regel 22 §Unknown is never 0).
  const freigebbarGesamt = summary ? summaryData.manual_review_pending : null;
  const hinweiseGesamt = summary ? (summaryData.manual_review_hints ?? 0) : null;
  // Die geladene Seite — nur fuer „showing X of N", nie fuer die Aktionen.
  const gezeigt = reviewQueue.length;
  const offenGesamt = (freigebbarGesamt != null && hinweiseGesamt != null)
    ? freigebbarGesamt + hinweiseGesamt
    : null;
  const gekuerzt = offenGesamt != null && offenGesamt > gezeigt;

  // 🔴 R15-W1 / R16-W8: der KPI-Dialog zeigt genau das, was die Kachel zaehlt —
  // freigebbare Zeilen. „These items require manual approval" stand bis
  // hierher ueber einer Liste, die auch Hinweise enthielt; die brauchen keine
  // Freigabe. Die Hinweise bleiben in der Warteschlange darunter sichtbar
  // (eigener Satz, kein „Accept"). Hinweise sortieren mit `severity='low'`
  // nach unten, die gefilterte Seite ist also vollstaendig — solange nicht
  // mehr als 20 freigebbare Zeilen offen sind; das sagt der Zusatz.
  const reviewQueueFreigebbar = reviewQueue.filter((i) => i.approvable === true);
  const dialogZusatz = (() => {
    const teile = [];
    if (reviewQueueFreigebbar.length < reviewQueue.length) {
      teile.push('hints are listed in the queue below and cannot be accepted here');
    }
    if (summary && reviewQueueFreigebbar.length < summaryData.manual_review_pending) {
      teile.push(`showing ${reviewQueueFreigebbar.length} of ${summaryData.manual_review_pending}`);
    }
    return teile.length ? ` · ${teile.join(' · ')}` : '';
  })();

  // 🔴 R16-B1: der Grund am Knopf ist ZUSTANDSABHAENGIG. „Nur Hinweise offen"
  // ist der erste Zustand nach dem Ausrollen (Plan E3: die vorhandenen
  // INT-Zeilen werden ohne Datenmigration zu Hinweisen) — und dort stand
  // „No prices are waiting for review." direkt ueber einer Liste, in der eine
  // Zeile steht. Der Satz behauptete, es warte nichts.
  const grundKeinAcceptAll = (() => {
    if (acceptingAll) return null;
    if (freigebbarGesamt == null) return 'Prices are still loading.';
    if (freigebbarGesamt > 0) return null;
    if ((hinweiseGesamt ?? 0) > 0) {
      return 'Only hints are waiting — nothing here can be accepted.';
    }
    // 🔴 R16-W-B: der Ausroll-Zweig. Der Server meldet 0/0, die Liste traegt
    // aber Zeilen (alte Serverfassung waehrend des Ausrollens, gekuerzte
    // Nutzlast). „No prices are waiting" ueber einer gelisteten Zeile waere
    // wieder B1 — dieselbe Form, nur im Umschaltfenster. Der Satz behauptet
    // deshalb NICHT, es warte nichts; „no prices" steht nur, wenn die Liste
    // wirklich leer ist.
    if (reviewQueue.length > 0) {
      return 'Prices are listed below, but the server did not report a total for them.';
    }
    return 'No prices are waiting for review.';
  })();

  const hasActivity = summaryData.total_changes_24h > 0
    || summaryData.manual_review_pending > 0
    // 🔴 Nur Hinweise offen: die Kachel zaehlt sie bewusst NICHT (E7 — „wartet
    // auf eine Entscheidung, die jemand treffen kann"), aber der Kasten muss
    // trotzdem erscheinen. Ohne diese Zeile waeren die Hinweise unsichtbar.
    || summaryData.manual_review_hints > 0
    || reviewQueue.length > 0;
  const isRunning = runStatus === 'running';

  return (
    <Panel pad="none" clip>
      {/* 🔴 W-33/D8: der Farbverlauf `linear-gradient(135deg, #667eea, #764ba2)`
          ist weg, samt `color: 'white'` an Kopfzeile, Knopf und Symbol. Der
          Verlauf trug keine Information, die die Zaehl-Pille nicht traegt: die
          Karte steht auf `PANEL_SX` mit einer Zahl daneben, und das ist die
          Auszeichnung, die der Rest der Seite schon benutzt.

          ⚠️ Abweichung von D8, ausdruecklich: "Run now" bleibt `outlined` und
          wird nicht zum Text-Knopf. D8 nennt fuer den Abschnittskopf einen
          `Button variant="text"` — hier haengt an diesem Knopf aber ein Lauf,
          der PREISE AENDERT, und die Regel des Repos fuer nicht
          zurueckholbare Vorgaenge ist, dass sie ihr Gewicht behalten (siehe
          `sperre` in dieser Datei). "Preview" daneben bleibt der leichte Knopf;
          die beiden duerfen nicht gleich aussehen.

          🔴 E7 (2026-10-07): die VARIANTE bleibt wie hier begruendet, die
          GROESSE nicht. `py: 0.5`/`FS.sm` an "Preview" und `py: 0.5` an
          "Run now" sind gefallen — die Karte traegt jetzt EINE Knopfhoehe
          (Theme `size="small"` plus Theme-Boden). "Preview" und "Run now"
          unterscheiden sich weiter ueber die Variante, nicht ueber die Hoehe. */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
          <AutoModeIcon sx={{ color: 'primary.main' }} />
          {/* 🔴 F8 (2026-10-08) → Nachbesserung 2 (R16 B1): wie in
              `ManualDriftCard` — der Abschnittsname steht wieder als `h6` UEBER
              der Karte (`HomeDashboard`), hier faellt er. Begruendung und die
              Messung zu `component="h6"` (redundant, `subtitle2` bildet MUI
              selbst auf `h6` ab) stehen dort. */}
          {summaryData.manual_review_pending > 0 && (
            <Pill tone="warn" count={summaryData.manual_review_pending} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: { xs: '1 1 100%', sm: 'none' } }}>
            {/* 🔴 Z-3: „Never" ist eine AUSSAGE („lief nie"), `—` heisst
                „unbekannt". Vorher stand hier bei einem 502 „Never", obwohl
                niemand das wissen konnte. */}
            Last run: {summary ? formatTime(summaryData.last_run_at) : '—'}
          </Typography>
          {/* 🔴 F6/T3-10 (2026-10-08): „Preview" und „Run now" kreisen um
              DENSELBEN Gegenstand (einen Automatik-Lauf, einmal trocken, einmal
              scharf) und standen als zwei gleichrangige Einzelknoepfe
              nebeneinander (T3-10). Jetzt EIN `SplitButton`: links die scharfe
              Aktion, rechts das Menue mit der Vorschau.
              🔴 Die Reihenfolge ist NICHT getauscht (anders als bei
              `ScraperList`, W17): „Run now" aendert Preise, hat aber keine
              eigene Rueckfrage, und die zerstörende Haelfte ist hier nicht die
              lautere — „Preview" ist der leichte Weg zu DEMSELBEN Lauf.
              🔴 `variant="outlined"`: die D8-Ausnahme bleibt (siehe der
              Kommentar ueber diesem Block) — der Lauf aendert Preise, der Knopf
              behaelt sein Gewicht. `Preview` sitzt jetzt IM Menue und ist
              damit der leisere Weg zu demselben Lauf.
              🔴 W9/W15 — der Ladezustand: `running={isRunning}` macht die linke
              Haelfte zum `ProgressButton`, `progress={null}` heisst
              „unbestimmt" (`SplitButton.jsx:133`, `ProgressButton.jsx:123`) — die
              Karte hat nur ein boolesches `isRunning`, einen Bruchteil gibt es
              nicht. Der Inline-`CircularProgress` im `startIcon` entfaellt
              damit: der Fuellbalken IST die Anzeige, und zwei Ladeanzeigen an
              einem Knopf sind genau das, was Anti-Pattern 11 verbietet. Das
              `startIcon` bleibt das Abspielsymbol — sichtbar, sobald der Lauf
              durch ist. */}
          <SplitButton
            label="Run now"
            onClick={() => triggerRun(false)}
            menu={[{
              key: 'preview',
              label: 'Preview',
              onClick: () => triggerRun(true),
              icon: <VisibilityIcon fontSize="small" />,
            }]}
            variant="outlined"
            startIcon={<PlayArrowIcon fontSize="small" />}
            disabled={isRunning}
            running={isRunning}
            progress={null}
          />
          {/* 🔴 `firstLoad`: diese Seite hat keinen Aufbauzustand — der Knopf
              dreht sich im ersten Abruf nicht mehr mit (Anti-Pattern 11). Der
              fehlende Aufbauzustand ist als Befund abgelegt
              (`docs/backlog-auto-repair/2026-10-07-aufbauzustand-ist-ein-spinner.md`),
              nicht mit einem zweiten Spinner zugedeckt. */}
          <RefreshButton
            ariaLabel="Refresh pricing data"
            loading={loading}
            firstLoad={loading && !summary}
            onClick={fetchData}
          />
        </Box>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              label="Auto-Applied (24h)"
              value={kpi(summaryData.auto_applied_24h)}
              icon={<CheckCircleIcon sx={{ color: 'success.main' }} />}
              valueColor={kpiFarbe('success.main')}
              onClick={summaryData.auto_applied_24h > 0 ? () => openStatModal({
                title: 'Auto-Applied (last 24h)',
                subtitle: 'Prices automatically updated by the scheduler',
                fetchUrl: '/api/pricing/automation/recent-changes?source=auto&since_hours=24&limit=100',
                itemType: 'change',
              }) : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              label="Need Review"
              value={kpi(summaryData.manual_review_pending)}
              icon={<WarningAmberIcon sx={{ color: 'warning.main' }} />}
              valueColor={kpiFarbe(summaryData.manual_review_pending > 0 ? 'warning.main' : undefined)}
              onClick={summaryData.manual_review_pending > 0 ? () => openStatModal({
                title: 'Pending Review Queue',
                subtitle: `These items require manual approval before applying${dialogZusatz}`,
                items: reviewQueueFreigebbar,
                itemType: 'review',
              }) : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              label="Total Changes (24h)"
              value={kpi(summaryData.total_changes_24h)}
              valueColor={kpiFarbe('text.secondary')}
              onClick={summaryData.total_changes_24h > 0 ? () => openStatModal({
                title: 'All Price Changes (last 24h)',
                subtitle: 'Every price update recorded in the last 24 hours',
                fetchUrl: '/api/pricing/automation/recent-changes?since_hours=24&limit=100',
                itemType: 'change',
              }) : undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatTile
              label="Errors (24h)"
              value={kpi(summaryData.errors_24h)}
              icon={summaryData.errors_24h > 0 ? <ErrorOutlineIcon sx={{ color: 'error.main' }} /> : undefined}
              valueColor={kpiFarbe(summaryData.errors_24h > 0 ? 'error.main' : 'success.main')}
              onClick={summaryData.errors_24h > 0 ? () => openStatModal({
                title: 'Shopify Sync Errors (last 24h)',
                subtitle: 'Price changes that failed to sync to Shopify',
                fetchUrl: '/api/pricing/automation/recent-changes?since_hours=24&errors_only=true&limit=100',
                itemType: 'change',
              }) : undefined}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Last Automated Run — persisted view */}
      <LastRunPanel onOpenDetail={setDetailChange} />

      {/* Manual run in progress */}
      {isRunning && (
        <>
          <Divider />
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={28} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {runIsDryRun ? 'Analysing recommendations…' : 'Applying price changes…'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This checks all recommendations — may take 30–60 seconds
            </Typography>
          </Box>
        </>
      )}

      {runStatus === 'error' && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ fontWeight: 600, mb: 0.5 }}>Pricing automation run failed</Box>
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  m: 0,
                  color: 'text.primary',
                }}
              >
                {runError || 'No error message — check the backend logs (purchase-order-generator service on Render).'}
              </Box>
            </Alert>
          </Box>
        </>
      )}

      {runStatus === 'done' && runResults && (
        <RunResultsPanel
          results={runResults}
          onApplyLive={handleApplyLive}
          applying={applying}
          onDecide={handleDecision}
          decidingId={decidingId}
          onOpenDetail={setDetailChange}
        />
      )}

      {/* Accepted (manually approved) — persists across refresh, sourced from price_change_log */}
      {acceptedChanges.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <SectionToggle
              icon={<CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />}
              label="Accepted"
              count={acceptedChanges.length}
              expanded={showAccepted}
              onToggle={() => setShowAccepted((v) => !v)}
            />
            <Collapse in={showAccepted}>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {acceptedChanges.map((c) => (
                  <PriceChange key={c.id} {...c} onClick={() => setDetailChange(c)} />
                ))}
              </Stack>
            </Collapse>
          </Box>
        </>
      )}

      {/* 🔴 Diese Meldung steht AUSSERHALB des `manual_review_pending`-Tors.
          Sie hing bis zum 2026-09-17 darin, und das hatte zwei Folgen:
          (a) `handleAcceptAll` ruft am Ende `fetchData()` -- meldet die
              Zusammenfassung danach 0, unmountet die Sektion und die Meldung
              "N von M Empfehlungen wurden NICHT angenommen" verschwindet MIT ihr
              (heute englisch: "N of M recommendations were NOT applied").
              Genau der Schaden, gegen den sie gebaut wurde, eine Ebene hoeher.
          (b) Faellt `/summary` aus, faellt `summaryData` auf Nullen und die
              Meldung kann gar nicht rendern -- ausgerechnet dann, wenn sie das
              Einzige waere, was sagt, dass ein Preis NICHT geschrieben wurde. */}
      {entscheidFehler && (
        <Alert
          severity={entscheidFehler.veraltet ? 'warning' : 'error'}
          onClose={() => setEntscheidFehler(null)}
          action={(entscheidFehler.veraltet || entscheidFehler.neuLaden) ? (
            <Button color="inherit" size="small" onClick={() => { setEntscheidFehler(null); fetchData(); }}>
              Reload
            </Button>
          ) : undefined}
          sx={{ mb: 1.5, fontSize: FS.md }}
        >
          {entscheidFehler.text}
        </Alert>
      )}

      {/* 🔴 R16-W3: die Erfolgsmeldung des Sammelwegs, wenn Hinweise
          uebersprungen wurden. Getrennt von `entscheidFehler` (das ist der
          Fehlerkanal) und ausserhalb des Tors darunter, damit sie einen
          Nachlauf-Abruf ueberlebt. */}
      {sammelMeldung && (
        <Alert severity="info" onClose={() => setSammelMeldung(null)} sx={{ mb: 1.5, fontSize: FS.md }}>
          {sammelMeldung}
        </Alert>
      )}

      {/* Pending Manual Reviews (from DB) */}
      {(summaryData.manual_review_pending > 0 || summaryData.manual_review_hints > 0 || reviewQueue.length > 0) && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>

            {/* 🔴 W7 (Regel 16): `flexWrap` an Box UND Typography. Die GEMESSENE
                Ursache ist der Umbruch MITTEN in der Angabe: ohne `flexWrap`
                bricht der Textknoten der Beschriftung bei 375 px intern um
                („Pending Review / Queue (34)"), die Zahl zerfaellt. Mit
                `flexWrap` wandern die Zusatz-Angaben als ganze Einheit in die
                naechste Zeile und die Beschriftung bleibt eine Zeile.
                ⚠️ Die frueher hier notierte Ueberlauf-Rechnung („~48 px zu viel")
                ist fuer das gerenderte Layout WIDERLEGT: gemessen bleibt
                `scrollWidth == clientWidth` (317/317) auch ohne die Eigenschaft,
                weil Text innerhalb seines Elements umbricht. Der Rotbeweis ist
                `textRects` (2 statt 1), nicht der Ueberlauf. Gemessen in
                `frontend/tests/design/ui2-preis-hinweis.spec.ts` (T.2). */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />
                {`Pending Review Queue (${offenGesamt ?? gezeigt})`}
                {/* 🔴 R16-B1: die Klammer ist die GESAMTZAHL der offenen Zeilen
                    (vom Server, ungekuerzt), der Zusatz ZERLEGT sie mit NAMEN.
                    „(1) + 1 hint" las sich wie 1 + 1 = 2; „1 to accept · 1 hint"
                    sagt, wovon die Rede ist. */}
                {hinweiseGesamt > 0 && (
                  <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                    {`· ${freigebbarGesamt} to accept · ${hinweiseGesamt} hint${hinweiseGesamt === 1 ? '' : 's'}`}
                  </Box>
                )}
                {/* 🔴 R16-W1: die Liste ist auf `limit=20` gekuerzt, die Zahlen
                    oben nicht. Ohne diesen Satz behauptete der Kopf eine
                    Vollstaendigkeit, die er nicht hat. */}
                {gekuerzt && (
                  <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                    {`· showing ${gezeigt} of ${offenGesamt}`}
                  </Box>
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* 🔴 E7 (2026-10-07): „Accept All" ist NICHT die Hauptaktion
                    der Karte — die steht in der einzelnen Zeile („Accept",
                    contained). Der Sammelknopf ist deshalb `outlined` und
                    traegt dieselbe Hoehe wie alle anderen Knoepfe der Karte;
                    `py: 0.25` und `FS.xs` sind gefallen, die machten ihn
                    28 px hoch neben 44-px-Nachbarn. */}
                <DisabledReason reason={grundKeinAcceptAll}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={acceptingAll || !(freigebbarGesamt > 0)}
                    onClick={handleAcceptAll}
                  >
                    {acceptingAll
                      ? <CircularProgress size={12} color="inherit" />
                      : `Accept All (${freigebbarGesamt ?? '—'})`}
                  </Button>
                </DisabledReason>
                <IconButton size="small" onClick={() => setShowReviewQueue((v) => !v)}
                  aria-label={`${showReviewQueue ? 'Hide' : 'Show'} review queue`}>
                  {showReviewQueue ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={showReviewQueue}>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {reviewQueue.map((item) => (
                  <ReviewItem
                    key={item.id}
                    {...item}
                    triggers={item.review_reasons}
                    onDecide={(action) => handleDecision(item.id, action)}
                    deciding={decidingId === item.id}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        </>
      )}

      {/* Empty state */}
      {/* 🔴 `!loading` davor. Ohne das rendert der LADEzustand als LEERzustand:
          vier Kacheln, die noch nichts aussagen, plus die Einladung, einen Lauf
          zu starten, der vielleicht gar nicht noetig ist. Regel 17 §1 im UI --
          "laedt gerade" und "es gibt nichts" sahen identisch aus. (Bis zum
          2026-10-10 standen die Kacheln dabei auf echten Nullen; seither auf
          `—`, siehe `kpi` oben.)
          🔴 Z-3 (2026-10-10): `summary &&` dazu. Ein 502 setzte `loading` auf
          `false` und `hasActivity` auf `false` — die Karte sagte dann „No
          automation activity yet", obwohl sie nichts gemessen hatte: derselbe
          Fehler-als-Leerzustand wie im Dialog (`StatDetailModal`). */}
      {!loading && summary && !hasActivity && runStatus === null && (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No automation activity yet. Click <strong>Run now</strong> to apply prices, or <strong>Preview</strong> to see what would happen first.
          </Typography>
        </Box>
      )}

      {sperre && (
        <Alert severity="warning" onClose={() => setSperre(null)} sx={{ mb: 2 }}>
          {/* 🔴 Die Kopfzeile kommt aus `warnungenAusListe`. Fest verdrahtet
              stand hier bis zum 2026-09-15 immer "below the minimum price" --
              bei `unter_produktboden` das Gegenteil des Befunds. */}
          <strong>{sperre.kopfzeile}</strong>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {/* 🔴 Der Index als Schluessel, nicht der Satz: zwei Zeilen ohne
                `market_code` erzeugen denselben Text und kollidieren. Die Liste
                wird nicht umsortiert, also ist der Index hier stabil.
                Vom Regel-16-Gate gemessen. */}
            {sperre.zeilen.map((z, i) => (
              <li key={i}>{z}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Change detail modal */}
      <ChangeDetailModal
        change={detailChange}
        open={Boolean(detailChange)}
        onClose={() => setDetailChange(null)}
      />

      {/* Stat detail modal (KPI number click) */}
      {statModal && (
        <StatDetailModal
          open={Boolean(statModal)}
          onClose={closeStatModal}
          title={statModal.title}
          subtitle={statModal.subtitle}
          fetchUrl={statModal.fetchUrl}
          items={statModal.items}
          itemType={statModal.itemType}
          onOpenDetail={(change) => setDetailChange(change)}
        />
      )}
    </Panel>
  );
}
